/**
 * HUPALUPA Grubu — patron paneli
 *
 * Grubun dört markası (HUPALUPA Store, HUPALUPA Eğlence, Kixx, Keramet Ilıcası)
 * platformda ayrı marka olarak duruyor ama tek şirkete ait; dördü de aynı
 * Business Manager'a (563972835291069) bağlı. Kixz FARKLI bir BM'de, gruba
 * dahil değil.
 *
 * Veri `/api/group/hupalupa/monthly` üzerinden tamamen CANLI gelir (Meta /
 * Google Ads / TikTok API'leri), veritabanına dokunulmaz — Keramet 18 Ağu
 * 2026'da açıldığı için DB'de hiç kaydı yok, canlıda Ağustos harcaması var.
 *
 * 🔴 Neden dört markaya ortak "gelir" sütunu YOK — bilerek böyle:
 * yalnızca Store satış yapıyor. Eğlence mesajla konuşma, Kixx üyelik lead'i
 * topluyor, Keramet sadece erişim alıyor. Ortak ciro sütunu koyulsaydı üç
 * markada ₺0 yazacak ve grup para yakıyormuş gibi görünecekti. Her marka
 * kendi KPI'ıyla (`kpi` alanı) gösteriliyor; sonuç bloğu ona göre değişir.
 * Eğlence'de Google'ın dönüşüm DEĞERİ ile dönüşüm SAYISI birebir aynı geliyor
 * (varsayılan 1 TL) — oradan ROAS türetmeyin, backend zaten yazmıyor.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Loader2, AlertTriangle, Download, Printer, ChevronDown,
  FileSpreadsheet, FileText, TrendingUp, TrendingDown, Minus, ExternalLink, Info, Sparkles,
  Mail, X, Check, Receipt,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const AY_SAYISI = 6;
const HAFTA_SAYISI = 8;

/**
 * Panelin dönem kipi ve API'den gelen dönem meta verisi (her dönemin tarih
 * aralığı, kaç günü geçtiği). Modül düzeyinde tutuluyor ki `ayBilgisi` /
 * `ayEtiketi` çağıran onlarca yere `veri` prop'u geçirmek gerekmesin;
 * `getir()` her yanıtta günceller.
 */
let DONEM_META = { mod: 'ay', harita: {} };

function donemAyarla(veri) {
  DONEM_META = {
    mod: veri?.period === 'hafta' ? 'hafta' : 'ay',
    harita: Object.fromEntries((veri?.periods || []).map((d) => [d.key, d])),
  };
}

/** Metinlerdeki "ay/hafta" çekimleri tek yerden. */
const D = {
  ad: () => (DONEM_META.mod === 'hafta' ? 'hafta' : 'ay'),
  Ad: () => (DONEM_META.mod === 'hafta' ? 'Hafta' : 'Ay'),
  yonelme: () => (DONEM_META.mod === 'hafta' ? 'haftaya' : 'aya'),
  lik: () => (DONEM_META.mod === 'hafta' ? 'haftalık' : 'aylık'),
  haftaMi: () => DONEM_META.mod === 'hafta',
};

/* Mecra görsel kimliği — /faturalama sayfasındaki logolarla aynı dosyalar. */
const MECRALAR = [
  { key: 'GOOGLE_ADS', label: 'Google', logo: '/google_ads-512.webp', color: '#4285F4' },
  { key: 'META_ADS', label: 'Meta', logo: '/6033716.png', color: '#0866FF' },
  { key: 'TIKTOK_ADS', label: 'TikTok', logo: '/tik-tokiconss.png', color: '#111827' },
];

/**
 * KPI tipine göre sonuç bloğunun etiketleri. Backend `kpi` alanını marka
 * tanımında sabitliyor; buradaki metinler yalnızca sunum.
 */
const KPI_METIN = {
  revenue: {
    sonucAd: 'Orders',
    birimAd: 'Cost / order',
    aciklama: "Sitede ölçülen (GA4) ciro ve sipariş — mecra beyanı kullanılmaz",
  },
  conversation: {
    sonucAd: 'Conversations',
    birimAd: 'Cost / conversation',
    aciklama: 'WhatsApp / DM üzerinden başlayan konuşma',
  },
  lead: {
    sonucAd: 'Leads',
    birimAd: 'Cost / lead',
    aciklama: 'Formu dolduran üyelik adayı',
  },
  reach: {
    sonucAd: null,
    birimAd: null,
    aciklama: 'Bu markada henüz dönüşüm ölçülmüyor — sadece erişim alınıyor',
  },
};

/* ── Biçimleyiciler ────────────────────────────────────────────────── */

const tl = (n, basamak = 0) =>
  n == null || !Number.isFinite(n)
    ? '—'
    : new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: basamak,
        maximumFractionDigits: basamak,
      }).format(n);

const paraTL = (n, basamak = 0) => (n == null || !Number.isFinite(n) ? '—' : `₺${tl(n, basamak)}`);

/** Büyük rakamları patron ekranında kısaltır: 1.749.981 → ₺1,75M */
const kisaTL = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `₺${tl(n / 1_000_000, 2)}M`;
  if (a >= 1_000) return `₺${tl(n / 1_000, 0)}K`;
  return `₺${tl(n, 0)}`;
};

const kisaSayi = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${tl(n / 1_000_000, 1)}M`;
  if (a >= 10_000) return `${tl(n / 1_000, 0)}K`;
  return tl(n, 0);
};

const gunOku = (t) => new Date(`${t}T00:00:00`);

/** "24–30 Ağu" / "31 Ağu – 6 Eyl" · uzun biçimde yıl da yazar. */
function haftaEtiketi(d, kisa) {
  const b = gunOku(d.start);
  const s = gunOku(d.end);
  const adlar = kisa ? AY_KISA : AY_ADLARI;
  const yil = kisa ? '' : ` ${s.getFullYear()}`;
  return b.getMonth() === s.getMonth()
    ? `${b.getDate()}–${s.getDate()} ${adlar[s.getMonth()]}${yil}`
    : `${b.getDate()} ${adlar[b.getMonth()]} – ${s.getDate()} ${adlar[s.getMonth()]}${yil}`;
}

const ayEtiketi = (key, kisa = false) => {
  const d = DONEM_META.harita[key];
  if (d && DONEM_META.mod === 'hafta') return haftaEtiketi(d, kisa);
  const [y, m] = String(key).split('-').map(Number);
  if (!y || !m) return key;
  return `${(kisa ? AY_KISA : AY_ADLARI)[m - 1]} ${y}`;
};

/** Dönem seçici düğmesi dar; haftada yalnızca başlangıç günü yazar. */
const donemDugmesi = (key) => {
  const d = DONEM_META.harita[key];
  if (d && DONEM_META.mod === 'hafta') {
    const b = gunOku(d.start);
    return `${b.getDate()} ${AY_KISA[b.getMonth()]}`;
  }
  return ayEtiketi(key, true);
};

/** Yüzde değişim; önceki dönem 0 ise oran hesaplanamaz (∞ göstermeyelim). */
const degisim = (simdi, once) => {
  if (once == null || simdi == null || once === 0) return null;
  return ((simdi - once) / Math.abs(once)) * 100;
};

/**
 * Ayın kaç günü geçti. İçinde bulunulan ay için bugünün günü, biten aylar için
 * ayın tamamı. API o ayı bugüne kadar verdiği için "geçen gün" = bugün.
 */
function ayBilgisi(key) {
  // Asıl kaynak API'den gelen dönem meta verisi (hafta kipinde tarihten
  // türetmek mümkün değil; kısmi haftayı sunucu hesaplıyor).
  const d = DONEM_META.harita[key];
  if (d) {
    return { gecenGun: d.gecenGun, toplamGun: d.toplamGun, devamEdiyor: d.devamEdiyor };
  }
  const [y, m] = String(key).split('-').map(Number);
  const bugun = new Date();
  const toplamGun = new Date(y, m, 0).getDate();
  const devamEdiyor = bugun.getFullYear() === y && bugun.getMonth() + 1 === m;
  return {
    gecenGun: devamEdiyor ? bugun.getDate() : toplamGun,
    toplamGun,
    devamEdiyor,
  };
}

/**
 * 🔴 Kısmi ayı dolu ayla kıyaslamak yanlış sinyal üretir: 18 günlük Ağustos'u
 * 31 günlük Temmuz'la karşılaştırınca panel "-27%" yazıyordu, oysa günlük hız
 * neredeyse aynıydı. İçinde bulunulan ay seçiliyse kıyas GÜNLÜK ORTALAMA
 * üzerinden yapılır; biten aylarda toplamlar doğrudan karşılaştırılır.
 */
function kiyasla(simdi, once, ay, onceAy) {
  if (simdi == null || once == null) return { yuzde: null, hizli: false };
  const a = ayBilgisi(ay);
  if (!a.devamEdiyor) return { yuzde: degisim(simdi, once), hizli: false };
  const o = ayBilgisi(onceAy);
  if (!a.gecenGun || !o.gecenGun) return { yuzde: null, hizli: false };
  return { yuzde: degisim(simdi / a.gecenGun, once / o.gecenGun), hizli: true };
}

/** Kısmi ayın günlük hıza göre ay sonu tahmini. */
const aySonuTahmini = (deger, ay) => {
  const a = ayBilgisi(ay);
  if (!a.devamEdiyor || !a.gecenGun || deger == null) return null;
  return (deger / a.gecenGun) * a.toplamGun;
};

/** Ay etiketi; sürmekte olan ay için kaçıncı günde olduğumuzu da yazar.
 *  Dışa aktarılan dosyayı paneli görmeden okuyan kişi "Ağustos düşük" diye
 *  yanlış sonuca varmasın diye kısmi ay her yerde işaretli. */
const ayBasligiTam = (key) => {
  const a = ayBilgisi(key);
  return a.devamEdiyor ? `${ayEtiketi(key)} (${a.gecenGun}/${a.toplamGun} gün)` : ayEtiketi(key);
};
const ayBasligiKisa = (key) => {
  const a = ayBilgisi(key);
  return a.devamEdiyor ? `${ayEtiketi(key, true)} · ${a.gecenGun}/${a.toplamGun}g` : ayEtiketi(key, true);
};

/* ── Excel biçimleri ───────────────────────────────────────────────── */

/**
 * Sayı biçimleri. Para birimi hücrenin İÇİNDE yazar (`1.234 ₺`) — dosyayı
 * açan kişiye "bu rakam TL mi, gösterim mi?" diye sorduracak hiçbir sütun
 * kalmasın istiyoruz.
 */
const XL_BICIM = {
  para: '#,##0" ₺"',
  para2: '#,##0.00" ₺"',
  adet: '#,##0',
  yuzde: '0.0"%"',
  kat: '0.00"×"',
};

const xlCizgi = { style: 'thin', color: { rgb: 'D9E0EA' } };
const xlKenar = { top: xlCizgi, bottom: xlCizgi, left: xlCizgi, right: xlCizgi };

const XL_STIL = {
  baslik: { font: { bold: true, sz: 15, color: { rgb: '0F172A' } } },
  altBaslik: { font: { sz: 10, color: { rgb: '64748B' } } },
  uyari: { font: { sz: 10, bold: true, color: { rgb: 'B45309' } } },
  bolum: {
    font: { bold: true, sz: 11, color: { rgb: '0F172A' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'E2E8F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  th: {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: xlKenar,
  },
  metin: { alignment: { horizontal: 'left', vertical: 'center' }, border: xlKenar },
  sayi: { alignment: { horizontal: 'right', vertical: 'center' }, border: xlKenar },
  metinSoluk: {
    font: { color: { rgb: '64748B' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  sayiSoluk: {
    font: { color: { rgb: '64748B' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  metinKalin: {
    font: { bold: true },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  sayiKalin: {
    font: { bold: true },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  grupMetin: {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  grupSayi: {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  ciro: {
    font: { bold: true, color: { rgb: '047857' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  not: { font: { sz: 10, color: { rgb: '334155' } }, alignment: { horizontal: 'left', vertical: 'top', wrapText: true } },
  notBaslik: { font: { sz: 10, bold: true, color: { rgb: '0F172A' } }, alignment: { horizontal: 'left', vertical: 'top' } },
};

/** Metin hücresi. */
const xt = (v, s = XL_STIL.metin) => ({ v: v == null ? '' : String(v), t: 's', s });

/**
 * Sayı hücresi. Ölçülmeyen değer Excel'e 0 olarak DEĞİL "—" olarak gider;
 * yoksa "bu markada dönüşüm ölçülmüyor" ile "sıfır dönüşüm aldı" aynı
 * görünür ve tablo yanlış okunur.
 */
const xn = (v, z = XL_BICIM.para, s = XL_STIL.sayi) =>
  v == null || !Number.isFinite(v) ? { v: '—', t: 's', s } : { v: Number(v), t: 'n', z, s };

/** Tüm satırı kaplayan bölüm başlığı (birleştirme için dolgu hücreleriyle). */
const xlBolumSatiri = (metin, genislik) => {
  const satir = Array.from({ length: genislik }, () => xt('', XL_STIL.bolum));
  satir[0] = xt(metin, XL_STIL.bolum);
  return satir;
};

/** Hücre dizilerinden çalışma sayfası kurar — stiller korunur. */
function xlSayfa(satirlar, cols, merges = [], rows = null) {
  const ws = {};
  let maxR = 0;
  let maxC = cols.length - 1;
  satirlar.forEach((satir, r) => {
    (satir || []).forEach((hucre, c) => {
      if (hucre == null) return;
      ws[XLSX.utils.encode_cell({ r, c })] = hucre;
      if (c > maxC) maxC = c;
    });
    maxR = r;
  });
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
  ws['!cols'] = cols;
  if (merges.length) ws['!merges'] = merges;
  if (rows) ws['!rows'] = rows;
  return ws;
}

/* ── Okuma notları — ekranda, Excel'in "Nasıl okunur" sayfasında ve PDF'te
      AYNI metin basılır. Kısmi ay örneği ("28/31g") sabit değil, elimizdeki
      aylardan üretilir; yoksa başlıkta 28/31 yazarken notta 18/31 diyen bir
      metin kalıyor ve okuyan "hangisi?" diye takılıyor. ─────────────────── */
function okumaNotlari(aylar = []) {
  const yarim = aylar.find((m) => ayBilgisi(m).devamEdiyor);
  const y = yarim ? ayBilgisi(yarim) : null;
  const isaret = y ? `${y.gecenGun}/${y.toplamGun}g` : DONEM_META.mod === 'hafta' ? '5/7g' : '18/31g';
  return [
    {
      baslik: 'Ciro ve sipariş GA4\'ten, sitenin kendi ölçümünden gelir.',
      metin:
        'Mecraların beyan ettiği ciro KULLANILMAZ: aynı satışı Google da Meta da kendine yazdığı ' +
        'için şişik çıkar (ölçüldü: altı ayda %25 fazla). Buradaki ciro, sipariş ve ROAS tek ' +
        'sayımdır — GA4 property 364875770.',
    },
    {
      baslik: "Ciro yalnızca HUPALUPA Store'da var.",
      metin:
        "Eğlence mesajla konuşma, Kixx üyelik lead'i topluyor, Keramet şu an sadece erişim alıyor. " +
        'Bu üç markada ROAS hesaplanmaz — mecranın dönüşüm değeri diye yazdığı rakam gerçek para ' +
        'değil, dönüşüm başına atadığı varsayılan 1 TL.',
    },
    {
      baslik: `Sürmekte olan ${D.ad()} eksik gündür.`,
      metin:
        `Başlığı "${isaret}" gibi işaretlidir; o ${D.ad()}daki değişim yüzdeleri toplamı değil ` +
        `GÜNLÜK ORTALAMAYI kıyaslar, yoksa yarım ${D.ad()} dolu ${D.ad()}ın yanında sahte düşüş gibi görünür. ` +
        'Bugünün rakamı da gün içinde artmaya devam eder; günlük ortalamayı bir miktar aşağı çeker ' +
        `(haftalık kipte etkisi daha büyüktür, çünkü ortalama beş güne bölünür).`,
    },
    {
      baslik: "Rakamlar canlı API'den geliyor, veritabanından değil.",
      metin:
        'Panel hiçbir yere kayıt yazmaz; on dakikalık önbellek vardır, yenile düğmesi onu atlar. ' +
        'Dosyanın üstündeki tarih, verinin çekildiği andır.',
    },
  ];
}

/* ── Küçük bileşenler ──────────────────────────────────────────────── */

function DegisimRozeti({ yuzde, tersMantik = false, hizli = false }) {
  if (yuzde == null || !Number.isFinite(yuzde)) {
    return <span className="text-[11px] text-slate-400">önceki ay yok</span>;
  }
  const artis = yuzde > 0.5;
  const azalis = yuzde < -0.5;
  // Harcamada artış nötr/uyarı, sonuçta artış iyi. `tersMantik` harcama için.
  const iyi = tersMantik ? azalis : artis;
  const kotu = tersMantik ? artis : azalis;
  const renk = iyi
    ? 'text-emerald-700 bg-emerald-50 ring-emerald-200'
    : kotu
    ? 'text-rose-700 bg-rose-50 ring-rose-200'
    : 'text-slate-600 bg-slate-50 ring-slate-200';
  const Ikon = artis ? TrendingUp : azalis ? TrendingDown : Minus;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium ring-1 ${renk}`}
      title={
        hizli
          ? `${D.Ad()} sürüyor — günlük ortalamaya göre kıyas`
          : `Önceki ${D.yonelme()} göre`
      }
    >
      <Ikon className="h-3 w-3" />
      {yuzde > 0 ? '+' : ''}
      {tl(yuzde, 0)}%
      {hizli && <span className="font-normal opacity-60">/gün</span>}
    </span>
  );
}

/** Mecra payını tek satırda gösteren yığılmış çubuk. */
function MecraCubugu({ platforms, toplam }) {
  if (!toplam) return <div className="h-1.5 w-full rounded-full bg-slate-100" />;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      {MECRALAR.map((m) => {
        const v = platforms?.[m.key]?.spend ?? platforms?.[m.key] ?? 0;
        if (!v) return null;
        return (
          <div
            key={m.key}
            style={{ width: `${(v / toplam) * 100}%`, backgroundColor: m.color }}
            title={`${m.label} · ${paraTL(v)}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Marka kartının içindeki MECRA KARTI — her mecra kendi kutusunda, logolu.
 *
 * 🔑 Efe: "meta ve google ads de ayrı ayrı gözüksün, her bir kartta logo da
 * olsun". Eskiden tek satırlık rozetlerdi (yalnızca tutar); mecra kırılımı
 * kartın kenarında kayboluyordu.
 *
 * 🔴 Burada CİRO/ROAS YAZILMAZ. Ciro sitenin kendi ölçümünden (GA4) gelir ve
 * marka seviyesindedir — mecraya bölünemez; mecraların beyan ettiği ciroyu
 * bölüştürmek aynı satışı iki mecraya birden yazmak olurdu. Mecra kartı
 * yalnızca kesin ölçülen şeyleri taşır: harcama, gösterim, tık, CTR.
 */
function MecraKarti({ mecra, veri, onceki, ay, oncekiAy }) {
  if (!veri?.spend) return null;
  // 🔴 `num()` backend yardımcısı, bu dosyada YOK — derlemeyi kırıyordu.
  const gosterim = Number(veri.impressions) || 0;
  const tik = Number(veri.clicks) || 0;
  const ctr = gosterim > 0 ? (tik / gosterim) * 100 : null;
  return (
    <div className="rounded-lg bg-slate-50 px-2.5 py-2 ring-1 ring-slate-200">
      <div className="flex items-center gap-1.5">
        <img src={mecra.logo} alt="" className="h-3.5 w-3.5 shrink-0 object-contain" />
        <span className="text-[11px] font-medium text-slate-600">{mecra.label}</span>
        <span className="ml-auto">
          <DegisimRozeti {...kiyasla(veri.spend, onceki?.spend, ay, oncekiAy)} tersMantik />
        </span>
      </div>
      <div className="mt-0.5 text-base font-bold tabular-nums leading-tight text-slate-900">
        {paraTL(veri.spend)}
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] tabular-nums text-slate-500">
        <span>{kisaSayi(gosterim)} impressions</span>
        <span>
          {kisaSayi(tik)} clicks{ctr == null ? '' : ` · %${tl(ctr, 2)}`}
        </span>
      </div>
    </div>
  );
}

/* ── Marka kartı ───────────────────────────────────────────────────── */

function MarkaKarti({ marka, ay, oncekiAy }) {
  const m = marka.months?.[ay];
  const o = oncekiAy ? marka.months?.[oncekiAy] : null;
  if (!m) return null;

  const metin = KPI_METIN[marka.kpi] || KPI_METIN.reach;
  const hata = marka.errors?.length ? marka.errors[0] : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200 print:break-inside-avoid">
      <div className="h-1" style={{ backgroundColor: marka.accent }} />

      {/* Logo kendi satırında: kartlar dar (dört sütun) ve logoların en-boy
          oranları çok farklı (Kixx 292×62, Keramet 1500×467). Logoyu adın
          yanına koyunca ada 120px kalıyor ve "HUPALUPA S…" diye kırpılıyordu. */}
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex h-8 items-center">
            <img
              src={marka.logo}
              alt={marka.label}
              className="max-h-8 max-w-[160px] object-contain object-left"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <a
            href={`https://${marka.site}`}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-slate-300 transition hover:text-slate-500 print:hidden"
            title={marka.site}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-2">
          <div className="text-sm font-semibold leading-tight text-slate-900">{marka.label}</div>
          <div className="text-xs text-slate-500">{marka.sub}</div>
        </div>
      </div>

      <div className="flex-1 px-4 py-3">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Spend</span>
          <DegisimRozeti {...kiyasla(m.spend, o?.spend, ay, oncekiAy)} tersMantik />
        </div>
        <div className="mt-0.5 text-2xl font-bold tabular-nums text-slate-900">{paraTL(m.spend)}</div>

        <div className="mt-2">
          <MecraCubugu platforms={m.platforms} toplam={m.spend} />
        </div>
        <div className="mt-2 space-y-1.5">
          {MECRALAR.map((mc) => (
            <MecraKarti
              key={mc.key}
              mecra={mc}
              veri={m.platforms?.[mc.key]}
              onceki={o?.platforms?.[mc.key]}
              ay={ay}
              oncekiAy={oncekiAy}
            />
          ))}
        </div>

        <div className="mt-3 border-t border-dashed border-slate-200 pt-3">
          {marka.kpi === 'revenue' ? (
            /* Ciro yedi haneli olabiliyor (₺1.337.177) ve eşit iki sütunda
               ROAS'ın üstüne biniyordu; ciro sütunu biraz geniş. */
            <div className="grid grid-cols-[1.35fr_1fr] gap-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Revenue · GA4
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-emerald-700">
                  {paraTL(m.revenue)}
                </div>
                <DegisimRozeti {...kiyasla(m.revenue, o?.revenue, ay, oncekiAy)} />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">ROAS</div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                  {m.roas ? `${tl(m.roas, 2)}×` : '—'}
                </div>
                <div className="text-[11px] text-slate-400">
                  {tl(m.result, 0)} sipariş · {paraTL(m.costPerResult)}/sipariş
                </div>
              </div>
            </div>
          ) : metin.sonucAd ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {metin.sonucAd}
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                  {tl(m.result, 0)}
                </div>
                <DegisimRozeti {...kiyasla(m.result, o?.result, ay, oncekiAy)} />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {metin.birimAd}
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                  {paraTL(m.costPerResult)}
                </div>
                <DegisimRozeti yuzde={degisim(m.costPerResult, o?.costPerResult)} tersMantik />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Impressions</div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                  {kisaSayi(m.impressions)}
                </div>
                <DegisimRozeti {...kiyasla(m.impressions, o?.impressions, ay, oncekiAy)} />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">CPM</div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                  {paraTL(m.cpm, 2)}
                </div>
                <div className="text-[11px] text-slate-400">
                  {kisaSayi(m.clicks)} tıklama · {paraTL(m.cpc, 2)}/tık
                </div>
              </div>
            </div>
          )}
          <div className="mt-2 flex items-start gap-1 text-[11px] leading-snug text-slate-400">
            <Info className="mt-px h-3 w-3 shrink-0" />
            <span>{metin.aciklama}</span>
          </div>
        </div>

        {hata && (
          <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 ring-1 ring-amber-200">
            <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
            <span>
              {hata.platform} · {hata.error}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Ekranda ve dışa aktarımda ortak kullanılan bloklar ────────────── */

/**
 * Grup özeti — dört kutu. PDF'te de aynı bileşen basıldığı için sütun
 * sayısı prop'la sabitlenebiliyor: ekran dışı (PDF) kabında medya sorguları
 * kabın değil TARAYICI penceresinin genişliğine bakar, dar ekranda çekilen
 * PDF iki sütuna düşerdi.
 */
function GrupOzeti({ veri, seciliAy, oncekiAy, dortSutun = false }) {
  const t = veri.totals?.[seciliAy];
  const o = oncekiAy ? veri.totals[oncekiAy] : null;
  if (!t) return null;
  const ayDurumu = ayBilgisi(seciliAy);
  const grupKiyas = o
    ? kiyasla(t.spend, o.spend, seciliAy, oncekiAy)
    : { yuzde: null, hizli: false };

  return (
          <div className={`grid gap-3 ${dortSutun ? 'grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
            <div className="rounded-xl bg-slate-900 px-4 py-3 text-white">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {ayEtiketi(seciliAy)} · Group spend
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">{paraTL(t.spend)}</div>
              <div className="mt-1.5">
                <MecraCubugu platforms={t.platforms} toplam={t.spend} />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-300">
                {MECRALAR.map((mc) =>
                  t.platforms?.[mc.key] ? (
                    <span key={mc.key} className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: mc.color }} />
                      {mc.label} {kisaTL(t.platforms[mc.key])}
                    </span>
                  ) : null
                )}
              </div>
              {ayDurumu.devamEdiyor && (
                <div className="mt-2 border-t border-white/10 pt-2 text-[11px] text-slate-400">
                  {D.Ad()} sürüyor — {ayDurumu.gecenGun}/{ayDurumu.toplamGun} gün.
                  Bu hızla {D.ad()} sonu{' '}
                  <strong className="text-slate-200">
                    ≈ {kisaTL(aySonuTahmini(t.spend, seciliAy))}
                  </strong>
                </div>
              )}
            </div>

            {/* Kısmi ayda kıyas günlük ortalamaya göre; yoksa 18 günlük Ağustos
                31 günlük Temmuz'un yanında sahte bir düşüş gibi görünüyor. */}
            <div className="flex flex-col justify-center rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {ayDurumu.devamEdiyor
                  ? `Günlük hız · önceki ${D.yonelme()} göre`
                  : `Önceki ${D.yonelme()} göre`}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                {grupKiyas.yuzde == null
                  ? '—'
                  : `${grupKiyas.yuzde > 0 ? '+' : ''}${tl(grupKiyas.yuzde, 1)}%`}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {o ? (
                  ayDurumu.devamEdiyor ? (
                    <>
                      {paraTL(t.spend / ayDurumu.gecenGun)}/gün ·{' '}
                      {ayEtiketi(oncekiAy, true) + ' '}
                      {paraTL(o.spend / ayBilgisi(oncekiAy).gecenGun)}/gün
                    </>
                  ) : (
                    `${ayEtiketi(oncekiAy, true)} ${paraTL(o.spend)}`
                  )
                ) : (
                  'kıyas yok'
                )}
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Revenue · measured on site (GA4)
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-700">
                {paraTL(t.revenue)}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                yalnızca HUPALUPA Store · diğer üçü satış yapmıyor
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Reach
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                {kisaSayi(t.impressions)}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                gösterim · {kisaSayi(t.clicks)} tıklama
              </div>
            </div>
          </div>
  );
}

/**
 * Izgarada markanın mecra satırlarının altına eklenen CİRO satırları.
 * GA4'ü olmayan markada boş dizi döner, tabloda hiç satır açılmaz —
 * "ölçülmüyor" ile "sıfır ciro" karışmasın.
 */
function ciroSatirlari(b) {
  const ga4Var = Object.values(b.months || {}).some((d) => d?.ga4Revenue != null);
  if (!ga4Var) return [];
  return [
    {
      ad: 'Ciro',
      rozet: 'GA4',
      kalin: true,
      renk: 'font-semibold text-emerald-700',
      deger: (d) => (d.revenue != null ? paraTL(d.revenue) : '—'),
    },
    {
      ad: 'Sipariş',
      rozet: 'GA4',
      renk: 'text-slate-600',
      deger: (d) => (d.ga4Orders != null ? tl(d.ga4Orders, 0) : '—'),
    },
  ];
}

/** Marka × mecra × ay harcama + ciro ızgarası. */
function IzgaraTablosu({ veri, seciliAy }) {
  return (
          <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
              <h2 className="text-sm font-semibold text-slate-900">
                Marka × Mecra × {D.Ad()} — harcama ve ciro
              </h2>
              <span className="text-[11px] text-slate-400">
                {ayEtiketi(veri.months[0], true)} – {ayEtiketi(veri.months[veri.months.length - 1], true)}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60 text-xs text-slate-500">
                    <th className="w-64 px-4 py-2 text-left font-medium">Marka / mecra</th>
                    {veri.months.map((m) => {
                      const a = ayBilgisi(m);
                      return (
                        <th
                          key={m}
                          className={`px-3 py-2 text-right font-medium ${
                            m === seciliAy ? 'bg-slate-900/5 text-slate-900' : ''
                          }`}
                        >
                          {ayEtiketi(m, true)}
                          {a.devamEdiyor && (
                            <span
                              className="ml-1 font-normal text-amber-600"
                              title={`${D.Ad()} sürüyor — ${a.gecenGun}/${a.toplamGun} gün. Dolu ${D.ad()}larla doğrudan kıyaslamayın.`}
                            >
                              {a.gecenGun}/{a.toplamGun}g
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {veri.brands.map((b) => {
                    const aktifMecralar = MECRALAR.filter((mc) =>
                      veri.months.some((m) => b.months[m].platforms?.[mc.key]?.spend)
                    );
                    return (
                      <React.Fragment key={b.id}>
                        <tr className="border-b border-slate-100 bg-white">
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-3 w-1 shrink-0 rounded-full"
                                style={{ backgroundColor: b.accent }}
                              />
                              <span className="font-semibold text-slate-900">{b.label}</span>
                            </div>
                          </td>
                          {veri.months.map((m) => (
                            <td
                              key={m}
                              className={`px-3 py-2 text-right font-semibold tabular-nums text-slate-900 ${
                                m === seciliAy ? 'bg-slate-900/5' : ''
                              }`}
                            >
                              {b.months[m].spend ? paraTL(b.months[m].spend) : '—'}
                            </td>
                          ))}
                        </tr>
                        {aktifMecralar.map((mc) => (
                          <tr key={mc.key} className="border-b border-slate-50 text-xs">
                            <td className="py-1.5 pl-9 pr-4">
                              <span className="inline-flex items-center gap-1.5 text-slate-500">
                                <img src={mc.logo} alt="" className="h-3.5 w-3.5 object-contain" />
                                {mc.label}
                              </span>
                            </td>
                            {veri.months.map((m) => {
                              const v = b.months[m].platforms?.[mc.key]?.spend;
                              return (
                                <td
                                  key={m}
                                  className={`px-3 py-1.5 text-right tabular-nums text-slate-600 ${
                                    m === seciliAy ? 'bg-slate-900/5' : ''
                                  }`}
                                >
                                  {v ? paraTL(v) : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}

                        {/* 🔴 Bu satırlar HARCAMA DEĞİL CİRO — aynı sütunda iki
                            birim var. Karışmasın diye yeşil zeminli, "ciro"
                            etiketli ve mecra satırlarından çizgiyle ayrı. */}
                        {ciroSatirlari(b).map((satir, i) => (
                          <tr
                            key={satir.ad}
                            className={`bg-emerald-50/40 text-xs ${
                              i === 0 ? 'border-t border-emerald-200' : 'border-b border-emerald-100/60'
                            }`}
                          >
                            <td className="py-1.5 pl-9 pr-4">
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                <span className={satir.kalin ? 'font-semibold text-slate-800' : 'text-slate-500'}>
                                  {satir.ad}
                                </span>
                                {satir.rozet && (
                                  <span className="rounded bg-emerald-100 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                                    {satir.rozet}
                                  </span>
                                )}
                              </span>
                            </td>
                            {veri.months.map((m) => (
                              <td
                                key={m}
                                className={`px-3 py-1.5 text-right tabular-nums ${satir.renk} ${
                                  m === seciliAy ? 'bg-slate-900/5' : ''
                                }`}
                              >
                                {satir.deger(b.months[m] || {})}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="px-4 py-2.5 font-bold text-slate-900">GRUP TOPLAM</td>
                    {veri.months.map((m) => (
                      <td
                        key={m}
                        className={`px-3 py-2.5 text-right font-bold tabular-nums text-slate-900 ${
                          m === seciliAy ? 'bg-slate-900/10' : ''
                        }`}
                      >
                        {paraTL(veri.totals[m].spend)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2 text-[11px] leading-snug text-slate-500">
              <span className="font-medium text-slate-700">Yeşil satırlar harcama değil ciro.</span>{' '}
              Ciro ve sipariş sitenin kendi ölçümüdür (GA4) — mecraların beyan ettiği ciro
              kullanılmaz, çünkü aynı satışı Google da Meta da kendine yazar. Yalnızca satış yapan
              markada (HUPALUPA Store) vardır. GRUP TOPLAM satırı yalnızca harcamadır.
            </div>
          </div>
  );
}

/** "Bu tablo nasıl okunur" bloğu — metinler `okumaNotlari` tek kaynağından. */
function OkumaNotu({ aylar }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600 ring-1 ring-slate-200">
      <div className="mb-1.5 font-semibold text-slate-800">Bu tablo nasıl okunur</div>
      <ul className="space-y-1">
        {okumaNotlari(aylar).map((n) => (
          <li key={n.baslik}>
            <strong className="text-slate-800">{n.baslik}</strong> {n.metin}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Yapay zekâ yorumu ─────────────────────────────────────────────
   `/api/group/hupalupa/yorum` panelin ızgarasıyla AYNI veriyi okur; burada
   görünen her sayı tablodakiyle birebir aynıdır. Model yalnızca CÜMLE KURAR:
   değişim yüzdeleri, günlük ortalamalar ve mecra payları backend'de
   hesaplanıp hazır veriliyor, yeni rakam üretmesi prompt'ta yasak.

   🔴 Panel açılışında KENDİLİĞİNDEN ÜRETİLMEZ (Efe: "sayfaya girer girmez
   yorumlamasın, tuşuna basınca raporlasın") — her açılışta 10-20 sn'lik bir
   model çağrısı başlatmanın anlamı yok. Kullanıcı "Yorumla"ya basar; dışa
   aktarımda dosyanın yorum sayfası boş kalmasın diye orada bir kez üretilir.

   🔴 Yorum SEÇİLİ döneme aittir. Ay/hafta değiştirildiğinde eldeki yorum o
   döneme ait değildir — bu yüzden dönem başına ayrı tutulur ve seçili dönemin
   yorumu yoksa kart "yorumla" düğmesi gösterir, eski yorumu göstermez. */

const YON_STILI = {
  iyi: { kutu: 'bg-emerald-50 ring-emerald-200', cizgi: 'bg-emerald-500', ikon: TrendingUp, ikonRenk: 'text-emerald-600' },
  kotu: { kutu: 'bg-rose-50 ring-rose-200', cizgi: 'bg-rose-500', ikon: TrendingDown, ikonRenk: 'text-rose-600' },
  notr: { kutu: 'bg-slate-50 ring-slate-200', cizgi: 'bg-slate-400', ikon: Minus, ikonRenk: 'text-slate-500' },
};

function YorumGovdesi({ yorum, pdf = false }) {
  const bulgular = yorum.bulgular || [];
  const aksiyonlar = yorum.aksiyonlar || [];
  const sorular = yorum.soruIsaretleri || [];
  return (
    <>
      <p className={`${pdf ? 'text-[13px]' : 'text-sm'} leading-relaxed text-slate-700`}>{yorum.ozet}</p>

      {bulgular.length > 0 && (
        <div className={`mt-3 grid gap-2 ${pdf ? 'grid-cols-2' : 'sm:grid-cols-2'}`}>
          {bulgular.map((b, i) => {
            const st = YON_STILI[b.yon] || YON_STILI.notr;
            const Ikon = st.ikon;
            return (
              <div key={i} className={`flex gap-2 rounded-lg px-3 py-2 ring-1 ${st.kutu}`}>
                <span className={`mt-0.5 w-0.5 shrink-0 rounded-full ${st.cizgi}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Ikon className={`h-3.5 w-3.5 shrink-0 ${st.ikonRenk}`} />
                    <span className="text-xs font-semibold text-slate-900">{b.baslik}</span>
                  </div>
                  {/* 🔴 Marka adında `uppercase` YOK: sarmalayıcı lang="tr" olduğu
                      için "Kixx" → "KİXX" diye noktalı i ile basılıyordu. */}
                  {b.marka ? (
                    <div className="mt-0.5 text-[10px] font-medium tracking-wide text-slate-500">
                      {b.marka}
                    </div>
                  ) : null}
                  <div className="mt-0.5 text-[11px] leading-relaxed text-slate-600">{b.metin}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {aksiyonlar.length > 0 && (
        <div className="mt-3 rounded-lg bg-slate-900/[0.03] px-3 py-2.5 ring-1 ring-slate-200">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Bu hafta yapılacaklar
          </div>
          <ol className="mt-1.5 space-y-1.5">
            {aksiyonlar.map((a, i) => (
              <li key={i} className="flex gap-2 text-[11px] leading-relaxed text-slate-700">
                {/* 🔴 PDF'te daire İÇİNDE rakam YOK: html2canvas küçük punto
                    metni kutunun dikey ortasına oturtmuyor, rakam daireden
                    taşıyor. Dosyada düz "1." yazılıyor, ekranda daire kalıyor. */}
                {pdf ? (
                  <span className="shrink-0 text-[11px] font-bold text-slate-900">{i + 1}.</span>
                ) : (
                  <span className="mt-[1px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                    {i + 1}
                  </span>
                )}
                <span>
                  <strong className="text-slate-900">{a.baslik}.</strong> {a.metin}
                  {a.marka ? <span className="text-slate-500"> ({a.marka})</span> : null}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {sorular.length > 0 && (
        <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900 ring-1 ring-amber-200">
          <span className="font-semibold">Dikkat: </span>
          {sorular.join(' · ')}
        </div>
      )}
    </>
  );
}

/**
 * Yorum kartı. `pdf` kipinde düğmeler ve canlı durumlar yoktur; yalnızca
 * üretilmiş yorum basılır.
 */
function YorumKarti({ yorum, yukleniyor, hata, yenile, donemEtiketi, pdf = false }) {
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-100">
            <Sparkles className="h-3.5 w-3.5 text-violet-600" />
          </span>
          <div>
            <div className="text-sm font-semibold text-slate-900">Yapay zekâ yorumu</div>
            <div className="text-[11px] text-slate-500">
              {donemEtiketi} · rakamlar panelin kendi verisinden, yorum Gemini
            </div>
          </div>
        </div>
        {!pdf && (
          <button
            onClick={yenile}
            disabled={yukleniyor}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-50 print:hidden"
            title="Yorumu yeniden üret"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} />
            {yorum ? 'Yeniden yorumla' : 'Yorumla'}
          </button>
        )}
      </div>

      <div className="mt-3">
        {yukleniyor && !yorum ? (
          /* İskelet: kart açılışta yer kaplasın, yorum gelince sayfa zıplamasın. */
          <div className="animate-pulse space-y-2">
            <div className="h-3 w-11/12 rounded bg-slate-100" />
            <div className="h-3 w-10/12 rounded bg-slate-100" />
            <div className="grid gap-2 pt-2 sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-14 rounded-lg bg-slate-50 ring-1 ring-slate-100" />
              ))}
            </div>
          </div>
        ) : hata && !yorum ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 ring-1 ring-rose-200">
            Yorum üretilemedi: {hata}
          </div>
        ) : !yorum ? (
          <div className="text-xs text-slate-500">
            Bu dönem için henüz yorum üretilmedi. “Yorumla” düğmesi dönemin rakamlarını okur
            ve değerlendirmeyi yazar (yaklaşık yarım dakika).
          </div>
        ) : (
          <YorumGovdesi yorum={yorum} pdf={pdf} />
        )}
      </div>

      {yorum && (
        <div className="mt-3 border-t border-slate-100 pt-2 text-[10px] leading-relaxed text-slate-400">
          {yorum.model} ile üretildi ·{' '}
          {new Date(yorum.uretildi).toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' })} ·
          model yalnızca yorumlar, rakamları panel hesaplar. Yatırım kararından önce tablodan doğrulayın.
        </div>
      )}
    </div>
  );
}

/** Okunamayan hesapların listesi. */
function HataListesi({ hatalar }) {
  if (!hatalar?.length) return null;
  return (
    <div className="rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-900 ring-1 ring-amber-200">
      <div className="mb-1 flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" /> Bazı hesaplar okunamadı
      </div>
      <ul className="space-y-0.5">
        {hatalar.map((h, i) => (
          <li key={i}>
            {h.marka} · {h.platform} · {h.account} — {h.error}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Dönemde yayınlanan reklamlar ──────────────────────────────────── */

/** Kart/dosya için kreatif görselinin tam adresi (proxy yolu göreli gelir). */
const kreatifGorsel = (reklam) =>
  reklam?.thumbnail
    ? reklam.thumbnail.startsWith('/')
      ? `${API_BASE}${reklam.thumbnail}`
      : reklam.thumbnail
    : null;

/**
 * PDF'e gömülecek kreatif görselini KÜÇÜLTÜR.
 *
 * 🔴🔴 Şart, süs değil: kağıttaki kare 40×40 basılıyor ama CDN'den gelen
 * görsel çoğu zaman 600×600. Dört markanın 414 kreatifi tam boy gömülünce
 * DOM'da onlarca MB base64 birikiyor, html2canvas bunları 28 sayfanın her
 * birinde yeniden çiziyor ve dışa aktarım DAKİKALARCA sürüyor (ölçüldü).
 * 96 px, 2× ölçekle basılan sayfa için fazlasıyla yeter.
 *
 * 🔑 `blob:` adresi kendi kaynağımızda olduğu için tuval "taint" olmuyor;
 * `toDataURL` sorunsuz çalışır.
 */
const PDF_KARE_PX = 96;
function gorseliKucult(blob) {
  return new Promise((coz) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      let sonuc = null;
      try {
        const en = img.naturalWidth;
        const boy = img.naturalHeight;
        const k = Math.min(1, PDF_KARE_PX / Math.max(en || 1, boy || 1));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(en * k));
        c.height = Math.max(1, Math.round(boy * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        sonuc = c.toDataURL('image/jpeg', 0.85);
      } catch (_) { /* küçültülemezse görselsiz basılır */ }
      URL.revokeObjectURL(url);
      coz(sonuc);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      coz(null);
    };
    img.src = url;
  });
}

const REKLAM_TIPI = {
  video: { ad: 'Video', renk: 'bg-violet-100 text-violet-700' },
  gorsel: { ad: 'Görsel', renk: 'bg-sky-100 text-sky-700' },
  metin: { ad: 'Metin', renk: 'bg-slate-100 text-slate-600' },
};

/** Bir platformda ilk açılışta gösterilen kreatif sayısı. */
const REKLAM_ILK = 12;

/** Tabloda kreatifin küçük karesi. */
function KreatifKare({ reklam }) {
  const [bozuk, setBozuk] = useState(false);
  /* 🔴 PDF'te `gomulu` (data: URI) ŞART: html2canvas çapraz kaynak görseli
     çizince canvas taint oluyor ve `toDataURL` bütün PDF'i düşürüyor. */
  const kaynak = reklam.gomulu || kreatifGorsel(reklam);
  if (!kaynak || bozuk) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-slate-100 text-[8px] font-medium text-slate-400 ring-1 ring-slate-200">
        {(REKLAM_TIPI[reklam.type] || REKLAM_TIPI.metin).ad}
      </div>
    );
  }
  return (
    <img
      src={kaynak}
      alt=""
      loading="lazy"
      className="h-10 w-10 shrink-0 rounded object-cover ring-1 ring-slate-200"
      onError={() => setBozuk(true)}
      /* 🔴 Meta proxy'si görsel bulamayınca HATA değil 1×1 saydam PNG
         döndürüyor; `onError` tetiklenmiyor. Gerçek genişliğe bakılır.
         `crossOrigin` KOYMA — çerez yetkisi gider, görsel kırılır. */
      onLoad={(e) => { if (e.currentTarget.naturalWidth <= 1) setBozuk(true); }}
    />
  );
}

/**
 * Bir markanın dönem içinde yayınlanan reklamları — MECRALAR TEK TABLODA.
 *
 * 🔑 Efe: "her mecra için ayrı tablolarda görüyor" → mecra ayrı blok değil
 * TEK SÜTUN. Sıralama harcamaya göre azalan: "bu ay param nereye gitti"
 * sorusu mecra sınırına takılmadan tek bakışta okunsun.
 */
/** Pivotun tek satırı — hem grup gövdesinde hem başka yerde aynı görünsün. */
function ReklamSatiri({ r, etkilesimVar, pdf = false }) {
  const m = r.metrics;
  const hucre = 'px-2 py-1.5 text-right tabular-nums whitespace-nowrap';
  return (
    <tr className="border-t border-slate-100">
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-2">
          <KreatifKare reklam={r} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {/* 🔴 PDF'te `line-clamp` KULLANILMAZ: html2canvas onu uygulamıyor,
                  metin kutuyu taşıyor ve satır yarım kesiliyor. Kağıtta metin
                  JS'te kısaltılır (`kisalt`) ve satır yüksekliğine pay verilir. */}
              {r.preview && !pdf ? (
                <a
                  href={r.preview}
                  target="_blank"
                  rel="noreferrer"
                  className="line-clamp-1 font-medium text-slate-800 hover:underline"
                  title={r.name}
                >
                  {r.name}
                </a>
              ) : (
                <span
                  className={
                    /* 🔴 PDF'te `overflow-hidden` YOK: html2canvas kutuyu metnin
                       gerçek satır kutusundan dar hesaplayıp harfleri üstten ve
                       alttan kesiyordu. Metin zaten `kisalt` ile kısaltıldığı
                       için taşma riski yok. */
                    pdf
                      ? 'block whitespace-nowrap font-medium leading-[15px] text-slate-800'
                      : 'line-clamp-1 font-medium text-slate-800'
                  }
                  title={r.name}
                >
                  {pdf ? kisalt(r.name, 52) : r.name}
                </span>
              )}
              {/* `active === false` ise işaretlenir; null "bilinmiyor" demek. */}
              {r.active === false && (
                <span className="shrink-0 rounded bg-slate-200 px-1 py-px text-[9px] font-medium text-slate-600">
                  durdu
                </span>
              )}
            </div>
            <div
              className={
                pdf
                  ? 'whitespace-nowrap text-[10px] leading-[14px] text-slate-400'
                  : 'line-clamp-1 text-[10px] text-slate-400'
              }
              title={r.campaign || ''}
            >
              {pdf ? kisalt(r.campaign || '—', 52) : r.campaign || '—'}
            </div>
          </div>
        </div>
      </td>
      <td className={`${hucre} font-semibold text-slate-800`}>
        {m ? paraTL(m.spend) : '—'}
        {m && !m.kesin && (
          <span
            className="text-slate-400"
            title="Google PMax/kampanya varlığı — bu kreatifin payı değil, göründüğü reklamın toplamı; toplanamaz"
          >
            *
          </span>
        )}
      </td>
      <td className={`${hucre} text-slate-600`}>{m ? tl(m.impressions) : '—'}</td>
      <td className={`${hucre} text-slate-600`}>
        {m ? `${tl(m.clicks)}${m.ctr == null ? '' : ` · %${tl(m.ctr, 2)}`}` : '—'}
      </td>
      {etkilesimVar && (
        <td
          className={`${hucre} ${
            m?.engagementCampaign ? 'font-semibold text-slate-800' : 'text-slate-600'
          }`}
        >
          {/* 🔴 Google'da etkileşim ÖLÇÜLMÜYOR → "—", sıfır değil. */}
          {m?.engagement == null
            ? '—'
            : `${tl(m.engagement)}${
                m.engagementRate == null ? '' : ` · %${tl(m.engagementRate, 2)}`
              }`}
        </td>
      )}
      <td className={`${hucre} text-slate-600`}>
        {m?.result == null ? '—' : `${tl(m.result)} ${kisalt(m.resultLabel || '', 18)}`}
      </td>
    </tr>
  );
}

/** Reklam yükünde marka id'si var; aylık veriyi ızgaradan eşleştirir. */
const izgaraMarka = (izgara, id) => (izgara?.brands || []).find((x) => x.id === id) || null;

/**
 * Marka × METRİK × DÖNEM tablosu — kreatiften bağımsız, ızgaranın verisinden.
 *
 * 🔑 Efe: "satırlarda metrikler, sütunlarda aylar olsun, aylık kıyaslama
 * yapabilelim; mecra kırılımı da olabilir." Kreatif pivotu tek döneme aitken
 * bu tablo markanın bütün dönemlerini yan yana koyar.
 *
 * 🔴 Ciro ve ROAS satırı YALNIZCA ciro ölçülen markada (Store) açılır —
 * GA4'ten gelir, mecra beyanı kullanılmaz. Diğer üç markada satır hiç
 * basılmaz; "₺0" yazmak "hiç satış yok" gibi okunurdu, oysa ölçülmüyor.
 *
 * 🔴 Mecra kırılımında CİRO YOK: GA4 site ölçümü marka seviyesinde, mecraya
 * bölünemez. Kırılım yalnızca harcama/gösterim/tıklamada var.
 */
function AylikKarsilastirma({ marka, aylar, seciliAy, pdf = false, kirilimZorla = false }) {
  /* Ekranda düğmeyle açılır. PDF'te düğme yok: kağıda hangi hâlinin gireceğini
     sayfa planı söyler (`kirilimZorla`) — marka sayfası kırılımı AÇIK basar,
     çünkü ekranda görünen her şey dosyaya da girmeli. */
  const [kirilim, setKirilim] = useState(false);
  const kirilimAcik = pdf ? kirilimZorla : kirilim;
  const metin = KPI_METIN[marka.kpi] || KPI_METIN.reach;

  /* 🔑 Hiç yayın olmayan dönem "0" değil "—" yazar — ızgaranın kuralı bu.
     Keramet Mart–Haziran'da hiç koşmamıştı; ₺0 yazmak "harcadık, sonuç
     alamadık" gibi okunuyordu. */
  const oku = (ay) => {
    const m = marka.months?.[ay];
    if (!m) return null;
    const bos = !m.spend && !m.impressions && !m.clicks;
    return bos ? null : m;
  };
  const oran = (pay, payda, carpan = 1) =>
    payda > 0 && pay != null ? (pay / payda) * carpan : null;

  const sayi = (v) => tl(v);
  const para = (v) => paraTL(v);
  const para2 = (v) => (v == null ? '—' : paraTL(v, 2));
  const yuzde = (v) => (v == null ? '—' : `%${tl(v, 2)}`);

  /* Her mecrada da geçerli olan çekirdek metrikler — tek yerde tanımlı ki
     marka bölümüyle mecra bölümleri aynı sırayı ve aynı biçimi kullansın. */
  const cekirdek = (al) => [
    { ad: 'Spend', deger: (m) => al(m)?.spend, bicim: para, kalin: true },
    { ad: 'Impressions', deger: (m) => al(m)?.impressions, bicim: sayi },
    /* 🔴 İzlenme eşiği MECRAYA GÖRE FARKLI: Meta 3 sn, TikTok oynatma
       (in-feed otomatik oynattığı için gösterime yakın çıkar — normal),
       Google TrueView. Her mecranın kendi panelindeki sayı; toplamı okurken
       aynı şeyi saymadıklarını unutma (tablo altında not var).
       `bosAtla`: video koşmayan mecrada satır hiç açılmaz, "0" gürültü olurdu. */
    { ad: 'Views', deger: (m) => al(m)?.views, bicim: sayi, bosAtla: true },
    { ad: 'Clicks', deger: (m) => al(m)?.clicks, bicim: sayi },
    { ad: 'CTR', deger: (m) => oran(al(m)?.clicks, al(m)?.impressions, 100), bicim: yuzde },
    { ad: 'CPM', deger: (m) => oran(al(m)?.spend, al(m)?.impressions, 1000), bicim: para2 },
    { ad: 'CPC', deger: (m) => oran(al(m)?.spend, al(m)?.clicks), bicim: para2 },
  ];

  const etkilesimVar = aylar.some((a) => oku(a)?.engagement != null);
  const izlenmeVar = aylar.some((a) => oku(a)?.views);

  // ── Marka (toplam) bölümü ────────────────────────────────────────
  const markaSatirlari = cekirdek((m) => m);
  if (etkilesimVar) {
    markaSatirlari.push({ ad: 'Engagement', deger: (m) => m?.engagement, bicim: sayi });
    markaSatirlari.push({ ad: 'Engagement rate', deger: (m) => m?.engagementRate, bicim: yuzde });
  }
  if (metin.sonucAd) {
    markaSatirlari.push({ ad: metin.sonucAd, deger: (m) => m?.result, bicim: sayi, kalin: true });
    markaSatirlari.push({ ad: metin.birimAd, deger: (m) => m?.costPerResult, bicim: para2 });
  }
  if (marka.kpi === 'revenue') {
    markaSatirlari.push({
      ad: 'Revenue · GA4', deger: (m) => m?.revenue, bicim: para, kalin: true, yesil: true,
    });
    markaSatirlari.push({
      ad: 'ROAS', deger: (m) => m?.roas, bicim: (v) => (v == null ? '—' : `${tl(v, 2)}×`), yesil: true,
    });
  }

  /* ── Mecra bölümleri ──────────────────────────────────────────────
     🔑 Efe: "mecra kırılımını metriklerin altında değil ÜSTÜNDE görmek
     istiyorum — Google'da böyle gerçekleşti, Meta'da böyle gerçekleşti
     şeklinde ayrı ayrı." Yani kırılım dıştaki gruplama; metrikler içeride.

     🔴 Mecra bölümünde ciro/ROAS ve marka KPI'ı YOK: ciro GA4'ten gelir ve
     marka seviyesindedir (mecraya bölünemez), KPI sonucu da öyle. Mecrada
     yalnızca o hesabın kendi ölçtükleri var. */
  /** Hiçbir dönemde değeri olmayan `bosAtla` satırlarını ele. */
  const ele = (liste) =>
    liste.filter((sat) => !sat.bosAtla || aylar.some((a) => sat.deger(oku(a))));

  const bolumler = [{ anahtar: 'marka', baslik: null, satirlar: ele(markaSatirlari) }];
  if (kirilimAcik) {
    for (const mc of MECRALAR) {
      const varMi = aylar.some((a) => oku(a)?.platforms?.[mc.key]?.spend);
      if (!varMi) continue;
      const al = (m) => m?.platforms?.[mc.key];
      const sat = cekirdek(al);
      const mecraEtkVar = aylar.some((a) => al(oku(a))?.engagement);
      if (mecraEtkVar) {
        sat.push({ ad: 'Engagement', deger: (m) => al(m)?.engagement, bicim: sayi });
        sat.push({
          ad: 'Engagement rate',
          deger: (m) => oran(al(m)?.engagement, al(m)?.impressions, 100),
          bicim: yuzde,
        });
      }
      bolumler.push({ anahtar: mc.key, mecra: mc, baslik: mc.label, satirlar: ele(sat) });
    }
  }

  const hucre = 'px-2 py-1.5 text-right tabular-nums whitespace-nowrap';

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-slate-700">
          Aylık karşılaştırma <span className="font-normal text-slate-400">· kreatiften bağımsız</span>
        </h3>
        {!pdf && (
          <button
            onClick={() => setKirilim((x) => !x)}
            className="rounded-lg px-2 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-800"
          >
            {kirilim ? 'Mecra kırılımını gizle' : 'Mecra kırılımı'}
          </button>
        )}
      </div>

      <div className="mt-1.5 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-[11px]">
          <thead>
            {/* lang="en": başlıklar İngilizce, sarmalayıcı lang="tr" olduğu
                için CSS `uppercase` noktalı İ basıyordu. */}
            <tr className="text-[10px] uppercase text-slate-400" lang="en">
              <th className="px-2 py-1 text-left font-medium">Metric</th>
              {aylar.map((a) => (
                <th
                  key={a}
                  className={`px-2 py-1 text-right font-medium ${a === seciliAy ? 'text-slate-800' : ''}`}
                >
                  {ayBasligiTam(a)}
                </th>
              ))}
            </tr>
          </thead>

          {bolumler.map((bol) => (
            <tbody key={bol.anahtar} className={bol.baslik ? 'border-t-2 border-slate-200' : ''}>
              {bol.baslik && (
                <tr className="bg-slate-50">
                  <td colSpan={aylar.length + 1} className="px-2 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <img src={bol.mecra.logo} alt="" className="h-3.5 w-3.5 object-contain" />
                      <span className="text-xs font-semibold text-slate-800">{bol.baslik}</span>
                    </span>
                  </td>
                </tr>
              )}
              {bol.satirlar.map((sat) => (
                <tr key={`${bol.anahtar}-${sat.ad}`} className="border-t border-slate-100">
                  <td className={`px-2 py-1.5 ${sat.kalin ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                    {sat.ad}
                  </td>
                  {aylar.map((a) => (
                    <td
                      key={a}
                      className={`${hucre} ${
                        sat.yesil ? 'text-emerald-700' : sat.kalin ? 'text-slate-900' : 'text-slate-600'
                      } ${sat.kalin ? 'font-semibold' : ''} ${a === seciliAy ? 'bg-slate-900/5' : ''}`}
                    >
                      {sat.bicim(sat.deger(oku(a)))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {izlenmeVar && (
        <div className="mt-1 text-[10px] text-slate-400">
          Views = her mecranın kendi panelindeki izlenme; eşikler aynı değil — Meta 3 saniye,
          TikTok oynatma (in-feed otomatik oynattığı için gösterime yakın çıkar), Google TrueView.
        </div>
      )}
      {kirilimAcik && (
        <div className="mt-1 text-[10px] text-slate-400">
          Mecra bölümlerinde ciro, ROAS ve {metin.sonucAd ? `"${metin.sonucAd}"` : 'sonuç'} yoktur:
          bunlar marka seviyesinde ölçülür (ciro GA4'ten gelir), mecraya bölünemez.
        </div>
      )}
      {marka.kpi === 'revenue' && !kirilimAcik && (
        <div className="mt-1 text-[10px] text-slate-400">
          Ciro ve ROAS sitenin kendi ölçümünden (GA4) gelir, mecra beyanı kullanılmaz.
        </div>
      )}
    </div>
  );
}

/**
 * Pivotun bir mecra grubu: TOPLAM SATIRI (tıklayınca açılır/kapanır) ve
 * altında o mecranın kreatifleri.
 *
 * 🔑🔑 Toplamlar nereden geliyor — karıştırma:
 *  - Harcama / gösterim / tık: IZGARADAN (mecra hesabının kendi rakamı).
 *    Satırlardan toplanamaz; Google'ın PMax/kampanya varlık satırları aynı
 *    harcamayı ve gösterimi tekrar tekrar taşıyor, toplamı 2-5 kat şişiyor.
 *  - Etkileşim: satırlardan toplanır — Meta/TikTok satırları birbirinden
 *    bağımsız (`kesin`), üst üste binmiyor. Google'da etkileşim yok → "—".
 *  - Sonuç: grubun bütün satırları `kesin` ise satırlardan toplanır (Meta,
 *    TikTok). Değilse (Google) ızgaranın dönüşüm sayısı yazılır — satırları
 *    toplamak PMax varlıklarını mükerrer sayardı.
 */
function MecraGrubu({ grup, etkilesimVar, sutunSayisi, pdf = false, sinir = REKLAM_ILK }) {
  const [acik, setAcik] = useState(true);
  const [hepsi, setHepsi] = useState(false);
  // Kağıtta her şey açık ve sabit sayıda: tıklanacak bir şey yok.
  const gorunen = pdf ? grup.ads.slice(0, sinir) : hepsi ? grup.ads : grup.ads.slice(0, REKLAM_ILK);
  const gosterilsin = pdf || acik;

  const t = grup.toplam || {};
  const hucre = 'px-2 py-2 text-right tabular-nums whitespace-nowrap';

  return (
    <tbody className="border-t-2 border-slate-200">
      <tr
        className={`bg-slate-50 ${pdf ? '' : 'cursor-pointer hover:bg-slate-100'}`}
        onClick={pdf ? undefined : () => setAcik((x) => !x)}
        title={pdf ? undefined : acik ? 'Kapat' : 'Aç'}
      >
        <td className="px-2 py-2">
          <div className="flex items-center gap-2">
            {!pdf && (
              <ChevronDown
                className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition ${acik ? '' : '-rotate-90'}`}
              />
            )}
            <img src={grup.mecra.logo} alt="" className="h-4 w-4 shrink-0 object-contain" />
            <span className="text-xs font-semibold text-slate-800">{grup.mecra.label}</span>
            {/* PDF'te bir mecra birden çok sayfaya taşabiliyor: sayı hep
                MECRANIN TAMAMI, sayfadaki dilim değil. Devam sayfasında
                başlık "devam" der ki toplam satırı ikinci kez okunmasın. */}
            <span className="text-[11px] text-slate-500">
              {tl(grup.tumSayi ?? grup.ads.length)} kreatif
            </span>
            {grup.devam && (
              <span className="rounded bg-slate-200 px-1 py-px text-[9px] font-medium text-slate-600">
                devam
              </span>
            )}
            {grup.hata && (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                <AlertTriangle className="h-3 w-3" /> okunamadı
              </span>
            )}
          </div>
        </td>
        <td className={`${hucre} text-xs font-bold text-slate-900`}>
          {t.spend == null ? '—' : paraTL(t.spend)}
        </td>
        <td className={`${hucre} text-xs font-semibold text-slate-700`}>
          {t.impressions == null ? '—' : tl(t.impressions)}
        </td>
        <td className={`${hucre} text-xs font-semibold text-slate-700`}>
          {t.clicks == null
            ? '—'
            : `${tl(t.clicks)}${t.ctr == null ? '' : ` · %${tl(t.ctr, 2)}`}`}
        </td>
        {etkilesimVar && (
          <td className={`${hucre} text-xs font-semibold text-slate-700`}>
            {t.engagement == null
              ? '—'
              : `${tl(t.engagement)}${
                  t.engagementRate == null ? '' : ` · %${tl(t.engagementRate, 2)}`
                }`}
          </td>
        )}
        <td className={`${hucre} text-xs font-semibold text-slate-700`}>
          {t.result == null ? '—' : `${tl(t.result)} ${kisalt(t.resultLabel || '', 18)}`}
        </td>
      </tr>

      {gosterilsin &&
        gorunen.map((r) => (
          <ReklamSatiri key={`${r.platform}-${r.id}`} r={r} etkilesimVar={etkilesimVar} pdf={pdf} />
        ))}

      {/* 🔑 Kağıtta "+N kreatif daha" satırı YOK: PDF artık listeyi kesmiyor,
          taşan satırlar devam sayfasına akıyor (`pdfKreatifSayfalari`). Bu
          satır geri konursa dosya eksikmiş gibi okunur. */}

      {!pdf && acik && grup.ads.length > REKLAM_ILK && (
        <tr className="border-t border-slate-100">
          <td colSpan={sutunSayisi} className="px-2 py-1.5">
            <button
              onClick={() => setHepsi((x) => !x)}
              className="text-[11px] font-medium text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
            >
              {hepsi
                ? `${grup.mecra.label} — daha az göster`
                : `${grup.mecra.label} — +${grup.ads.length - REKLAM_ILK} kreatif daha`}
            </button>
          </td>
        </tr>
      )}
    </tbody>
  );
}

/**
 * Bir markanın dönem içinde yayınlanan reklamları — MECRA MECRA PİVOT.
 *
 * 🔑 Efe: "her marka kartının içinde Google Ads ve Meta'yı birbirinden ayır,
 * pivot gibi görelim." Tek tablo ama mecra başına grup başlığı + kendi
 * kreatifleri; gruplar harcamaya göre sıralı, grup içi de öyle.
 *
 * 🔑🔑 Grup toplamlarının kaynağı `MecraGrubu`'nda ayrıntılı yazılı: harcama/
 * gösterim/tık IZGARADAN gelir, satırlar toplanarak DEĞİL — Google'ın PMax/
 * kampanya varlık satırları aynı rakamı tekrar taşıyor, toplanırsa 2-5 kat şişer.
 */
/**
 * Bir markanın kreatiflerini MECRA MECRA gruplar ve grup toplamlarını kurar.
 *
 * Modül seviyesinde: ekrandaki tablo da, PDF sayfa planlayıcısı da aynı
 * gruplamayı görmek zorunda — kağıda hangi satırın gireceğini hesaplayan yer
 * ile basan yer ayrı listeler kursaydı sayfa sınırları kayardı.
 */
function mecraGruplari(marka, mecraToplam) {
  const out = [];
  for (const mc of MECRALAR) {
    const p = marka.platforms?.[mc.key];
    const ads = [...(p?.ads || [])].sort(
      (a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0)
    );
    if (!ads.length && !p?.error) continue;

    // Izgaranın mecra rakamı: harcama/gösterim/tık burada kesin ve tam.
    const iz = mecraToplam?.[mc.key] || null;
    const gosterim = iz?.impressions ?? null;
    const tik = iz?.clicks ?? null;

    /* Etkileşim satırlardan toplanır (Meta/TikTok satırları bağımsız);
       hiç ölçülmüyorsa null kalır ve başlıkta "—" yazar. */
    const etkilesimliler = ads.filter((r) => r.metrics?.engagement != null);
    const etkilesim = etkilesimliler.length
      ? etkilesimliler.reduce((n, r) => n + r.metrics.engagement, 0)
      : null;

    /* Sonuç: bütün satırlar `kesin` ise satırlardan toplanır; değilse
       (Google'da PMax varlıkları var) ızgaranın dönüşüm sayısı yazılır —
       satırları toplamak varlıkları mükerrer sayardı. */
    const hepsiKesin = ads.every((r) => !r.metrics || r.metrics.kesin);
    const sonucluSatir = ads.find((r) => r.metrics?.resultLabel);
    const sonuc = hepsiKesin
      ? ads.reduce((n, r) => n + (r.metrics?.result ?? 0), 0)
      : iz?.conversions ?? null;

    out.push({
      mecra: mc,
      ads,
      hata: p?.error || null,
      toplam: {
        spend: iz?.spend ?? null,
        impressions: gosterim,
        clicks: tik,
        ctr: gosterim > 0 && tik != null ? (tik / gosterim) * 100 : null,
        engagement: etkilesim,
        engagementRate:
          etkilesim != null && gosterim > 0 ? (etkilesim / gosterim) * 100 : null,
        result: sonuc,
        resultLabel: hepsiKesin ? sonucluSatir?.metrics?.resultLabel || '' : 'Conversions',
      },
    });
  }
  // Çok harcayan mecra üstte.
  return out.sort((a, b) => (b.toplam?.spend || 0) - (a.toplam?.spend || 0));
}

function MarkaReklamTablosu({
  marka, mecraToplam, pdf = false, sinir = REKLAM_ILK,
  /* PDF'te tablo sayfalara bölünür: `gruplarHazir` o sayfaya düşen dilimdir,
     `bayraklar` ise MARKANIN TAMAMINDAN hesaplanır. Sütun sayısı ve dipnotlar
     sayfadan sayfaya değişemez — 2. sayfada Engagement sütunu kaybolsaydı
     tablo başka bir tablo gibi okunurdu. */
  gruplarHazir = null, bayraklar = null,
}) {
  const hesap = useMemo(() => mecraGruplari(marka, mecraToplam), [marka, mecraToplam]);
  const gruplar = gruplarHazir || hesap;

  const tumSatirlar = useMemo(() => gruplar.flatMap((g) => g.ads), [gruplar]);
  const etkilesimVar = bayraklar
    ? bayraklar.etkilesimVar
    : tumSatirlar.some((r) => r.metrics?.engagement != null);
  const toplanamazVar = bayraklar
    ? bayraklar.toplanamazVar
    : tumSatirlar.some((r) => r.metrics && !r.metrics.kesin);
  /* 🔴 "Purchases" etiketi hücreye sığsın diye kısaldı; mecra beyanı olduğu
     uyarısı BURADA duruyor. Kreatif seviyesinde GA4 yok — atıf o derinliğe
     inmiyor — o yüzden satış sayısı mecranın kendi beyanı. Kaldırma. */
  const beyanSatisVar = bayraklar
    ? bayraklar.beyanSatisVar
    : tumSatirlar.some((r) => r.metrics?.resultLabel === 'Purchases');

  if (!tumSatirlar.length) {
    return (
      <div className="text-[11px] text-slate-400">
        Bu markada bu dönemde reklam yayınlanmamış.
      </div>
    );
  }

  const sutunSayisi = etkilesimVar ? 6 : 5;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] border-collapse text-[11px]">
          <thead>
            {/* 🔴 lang="en": başlıklar İngilizce ve CSS `uppercase` var;
                sarmalayıcı lang="tr" olduğu için "Clicks" → "CLİCKS",
                "Impressions" → "IMPRESSİONS" diye noktalı İ ile büyüyordu. */}
            <tr className="text-[10px] uppercase text-slate-400" lang="en">
              <th className="px-2 py-1 text-left font-medium">Creative</th>
              <th className="px-2 py-1 text-right font-medium">Spend</th>
              <th className="px-2 py-1 text-right font-medium">Impressions</th>
              <th className="px-2 py-1 text-right font-medium">Clicks · CTR</th>
              {etkilesimVar && (
                <th className="px-2 py-1 text-right font-medium">Engagement · rate</th>
              )}
              <th className="px-2 py-1 text-right font-medium">Result</th>
            </tr>
          </thead>
          {gruplar.map((g) => (
            <MecraGrubu
              key={g.mecra.key}
              grup={g}
              etkilesimVar={etkilesimVar}
              sutunSayisi={sutunSayisi}
              pdf={pdf}
              sinir={sinir}
            />
          ))}
        </table>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
        {toplanamazVar && (
          <span className="text-[10px] text-slate-400">
            * Google PMax/kampanya varlığı — rakam bu kreatifin payı değil, göründüğü reklamın
            toplamıdır; bu satırlar birbirine eklenmez. Grup başlığındaki harcama mecranın
            gerçek toplamıdır.
          </span>
        )}
        {etkilesimVar && (
          <span className="text-[10px] text-slate-400">
            Etkileşim = reaksiyon + yorum + paylaşım + kayıt; Google'da ölçülmez.
          </span>
        )}
        {beyanSatisVar && (
          <span className="text-[10px] text-slate-400">
            Purchases = mecranın beyanı — kreatif seviyesinde GA4 ölçümü yok. Sitede ölçülen
            ciro/sipariş için yukarıdaki aylık tabloya bakın.
          </span>
        )}
      </div>
    </div>
  );
}

function YayindakiReklamlar({
  veri, yukleniyor, hata, yenile, donemEtiketi,
  mecraToplamlari, izgara, seciliAy,
}) {
  /* 🔑 Efe: "4 tane 'yayınlanan reklamlar' kartı olsun, her markaya özel —
     tek bir kartta hepsi verilmesin." Her marka KENDİ kartında; ortak başlık
     yalnızca ince bir satır (yenile + toplam), kart değil. */
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <p className="max-w-3xl text-[11px] text-slate-500">
          Aşağıdaki kartlar {donemEtiketi} döneminde gösterim almış bütün kreatifleri gösterir —
          yukarıdaki harcamayla aynı dönem. Bugün duraklatılmış olanlar da listede,{' '}
          <em className="not-italic">durdu</em> etiketiyle. Mecra hesaplarından canlı okunur,
          çok harcayan üstte.
        </p>
        <div className="flex items-center gap-2">
          {veri && (
            <span className="text-[11px] text-slate-400">
              {tl(veri.total, 0)} kreatif ·{' '}
              {veri.cached ? 'önbellekten' : `${(veri.elapsedMs / 1000).toFixed(1)} sn`}
            </span>
          )}
          <button
            onClick={yenile}
            disabled={yukleniyor}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
            title="Canlı yenile"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {yukleniyor && !veri && (
        <div className="mt-2 flex items-center gap-2 rounded-xl bg-white px-4 py-6 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          Dört markanın dönem içinde yayınlanan reklamları okunuyor…
        </div>
      )}

      {hata && !veri && (
        <div className="mt-2 rounded-xl bg-white px-4 py-5 text-sm text-rose-700 shadow-sm ring-1 ring-slate-200">
          Reklam listesi alınamadı: {hata}
        </div>
      )}

      {veri?.brands?.map((b) => (
        <div
          key={b.id}
          className="mt-3 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200 print:break-inside-avoid"
        >
          {/* Marka rengi kartın üstünde: dört kart alt alta, hangisi olduğu
              başlığı okumadan da ayrışsın. */}
          <div className="h-1" style={{ backgroundColor: b.accent }} />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 px-4 py-2.5">
            {/* 🔴 Logoların en-boy oranları çok farklı (Kixx 292×62, Keramet
                1500×467) → yüksekliği sabitle, genişliği sınırla; yoksa
                başlık satırını dağıtıyor. Yüklenemezse gizlenir, ad kalır. */}
            {b.logo && (
              <img
                src={b.logo}
                alt=""
                className="mr-1 max-h-6 max-w-[110px] shrink-0 object-contain object-left"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <h2 className="text-sm font-semibold text-slate-900">{b.label}</h2>
            <span className="text-xs text-slate-400">{b.sub}</span>
            {izgara?.months?.length && (
              <span className="ml-auto text-[11px] text-slate-400">
                {ayBasligiTam(izgara.months[0])} – {ayBasligiTam(izgara.months[izgara.months.length - 1])}
              </span>
            )}
          </div>

          {/* Önce dönemler arası kıyas (kreatiften bağımsız), sonra seçili
              dönemin kreatifleri: "nasıl gidiyor" sorusu "neyle çıktık"tan
              önce geliyor. */}
          {izgaraMarka(izgara, b.id) && (
            <div className="border-b border-slate-100 px-4 py-3">
              <AylikKarsilastirma
                marka={izgaraMarka(izgara, b.id)}
                aylar={izgara.months}
                seciliAy={seciliAy}
              />
            </div>
          )}

          <div className="px-4 py-3">
            <div className="mb-2 text-xs font-semibold text-slate-700">
              {donemEtiketi} döneminde yayınlanan reklamlar{' '}
              <span className="font-normal text-slate-400">
                · {b.total ? `${tl(b.total, 0)} kreatif` : 'yok'}
              </span>
            </div>
            <MarkaReklamTablosu marka={b} mecraToplam={mecraToplamlari?.[b.id]} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Dışa aktarım ──────────────────────────────────────────────────── */

/** Dışa aktar menüsündeki satır — ne indireceğini açıklamasıyla söyler. */
function MenuSatiri({ ikon: Ikon, baslik, aciklama, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition hover:bg-slate-50"
    >
      <Ikon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <span>
        <span className="block text-xs font-semibold text-slate-800">{baslik}</span>
        <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{aciklama}</span>
      </span>
    </button>
  );
}

/**
 * PDF sayfası A4 yatayın TAM piksel karşılığı (297 × 210 mm @ 96 dpi).
 * Blok sayfayla aynı orana sabitlenmezse jsPDF görüntüyü orana göre
 * sığdırır ve sayfanın altında/yanında boş bant kalır — ilk sürümün
 * bozuk görünme sebebi buydu.
 */
const PDF_GENISLIK = 1123;
const PDF_YUKSEKLIK = 794;

/**
 * İçeriği A4 sayfasına oturtur. Doğal yüksekliği ölçer; taşıyorsa içeriği
 * `1/k` kadar GENİŞLETİP `k` ile küçültür — yalnızca küçültseydik içerik
 * daralır, sayfanın sağında boş bant kalırdı. Genişletme yeniden akıtır,
 * o yüzden gereken yükseklik ölçülenden azalır; kutuya `PDF_YUKSEKLIK / k`
 * verildiği için kırpma olmaz.
 */
function sayfayaSigdir(kok) {
  const ic = kok?.querySelector('[data-pdf-ic]');
  if (!ic) return;
  /* 🔴🔴 TEK GEÇİŞ YETMİYOR. Ölçek küçüldükçe kap GENİŞLETİLİYOR (küçültme
     sonrası sayfa eni tam dolsun diye); genişleyen kapta `aspect-square`
     kreatif kareleri de büyüyor ve gereken yükseklik yeniden artıyor. Tek
     geçişte kreatif sayfasının son mecra satırı sayfanın altında KESİLİYORDU.
     Ölçek oturana kadar en çok dört kez ölçülür. */
  let k = 1;
  for (let i = 0; i < 6; i++) {
    ic.style.transform = 'none';
    ic.style.width = `${Math.round(PDF_GENISLIK / k)}px`;
    ic.style.height = 'auto';
    const gereken = ic.scrollHeight;
    const hedef = PDF_YUKSEKLIK / k;
    if (gereken <= hedef + 1) break;
    k *= hedef / gereken;
  }
  ic.style.width = `${Math.round(PDF_GENISLIK / k)}px`;
  ic.style.height = `${Math.round(PDF_YUKSEKLIK / k)}px`;
  ic.style.transformOrigin = 'top left';
  ic.style.transform = k < 1 ? `scale(${k})` : 'none';
}

/**
 * PDF'e giren kreatif sayısı — mecra başına. Tam liste Excel'de.
 *
 * 🔴🔴 SABİT SAYI OLMAZ, mecra sayısına bağlı. Kağıda sığdırma işi ölçekle
 * çözülemiyor: kap genişletilip küçültülünce içeriğin EN-BOY ORANI aynı
 * kalıyor (ızgara `aspect-square` karelerden oluşuyor), yani sayfadan uzun
 * bir blok hiçbir ölçekte sığmıyor — üç mecralı markada (Eğlence) TikTok
 * satırı sayfanın altında kesiliyordu. Çözüm satır sayısını sınırlamak:
 * ızgara 8 sütun, aşağıdaki sayılar mecra başına en çok iki satır demek.
 */
/**
 * Kağıttaki kreatif tablosunda bir sayfaya kaç SATIR girer.
 *
 * 🔑🔑 Kreatif listesi PDF'te ARTIK KESİLMİYOR (Efe: "pdf export eksiksiz
 * olmalı"). Eskiden mecra başına 6 kreatif basılıp gerisi "tam liste
 * Excel'de" diye geçiliyordu; Store'da 301 kreatifin 12'si kağıda giriyordu.
 * Şimdi taşan satırlar devam sayfalarına akıyor.
 *
 * 🔴 Sayı deneyle bulundu: satırlar `sayfayaSigdir` ile küçültülüyor, 26'nın
 * üstünde yazı okunmaz hale geliyor. Satır yüksekliği ya da yazı boyutu
 * değişirse YENİDEN ÖLÇ — kağıdı basıp bir sayfayı yakınlaştırarak.
 */
const PDF_SATIR_BUTCE = 26;

/** Bir grup başlığının satır cinsinden maliyeti (başlık + tablo payı). */
const PDF_GRUP_MALIYETI = 2;

/**
 * Bir markanın mecra gruplarını kağıt sayfalarına böler — hiçbir kreatif
 * düşmeden. Bir mecra sayfaya sığmazsa devam sayfasında kendi başlığıyla
 * (ve "devam" etiketiyle) sürer; başlıktaki toplam hep mecranın TAMAMIDIR.
 */
function pdfKreatifSayfalari(gruplar, tavan = PDF_SATIR_BUTCE) {
  /* 🔑 Satırlar sayfalara EŞİT dağıtılır, "doldur-taşır" mantığıyla değil.
     Yoksa son sayfada üç satır kalıyor ve `sayfayaSigdir` o sayfayı
     küçültmediği için yazı bir önceki sayfanın iki katı boyutta çıkıyordu —
     aynı listenin sayfaları farklı puntoda görünüyordu. */
  const toplam = gruplar.reduce(
    (n, g) => n + Math.max(g.ads.length, 1) + PDF_GRUP_MALIYETI,
    0
  );
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / tavan));
  const butce = Math.ceil(toplam / sayfaSayisi);

  const sayfalar = [];
  let sayfa = [];
  let kalan = butce;
  const kapat = () => {
    if (sayfa.length) sayfalar.push(sayfa);
    sayfa = [];
    kalan = butce;
  };

  for (const g of gruplar) {
    // Yalnızca "okunamadı" satırı olan mecra: tek başlık, kreatif yok.
    if (!g.ads.length) {
      if (kalan <= PDF_GRUP_MALIYETI) kapat();
      sayfa.push({ ...g, tumSayi: 0, devam: false });
      kalan -= PDF_GRUP_MALIYETI;
      continue;
    }
    let i = 0;
    while (i < g.ads.length) {
      // Yeni gruba en az bir satır sığmıyorsa sayfayı kapat.
      if (kalan <= PDF_GRUP_MALIYETI + 1) kapat();
      const yer = Math.max(1, kalan - PDF_GRUP_MALIYETI);
      const dilim = g.ads.slice(i, i + yer);
      sayfa.push({ ...g, ads: dilim, tumSayi: g.ads.length, devam: i > 0 });
      kalan -= dilim.length + PDF_GRUP_MALIYETI;
      i += dilim.length;
    }
  }
  kapat();
  return sayfalar;
}

/** A4 yatay ölçüsünde tek bir PDF sayfası kabı. */
function PdfSayfa({ children }) {
  return (
    <div
      data-pdf-sayfa
      style={{
        width: PDF_GENISLIK,
        height: PDF_YUKSEKLIK,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <div data-pdf-ic className="flex flex-col px-9 py-7">
        {children}
      </div>
    </div>
  );
}

/** Marka sayfalarının üstündeki renk şeridi + logo + dönem satırı. */
function PdfMarkaSeridi({ marka, donemEtiketi, sag }) {
  return (
    <div className="mt-3 flex items-center gap-2">
      <span className="h-4 w-1 shrink-0 rounded-full" style={{ backgroundColor: marka.accent }} />
      {marka.logo && (
        <img
          src={marka.logo}
          alt=""
          className="max-h-5 max-w-[90px] shrink-0 object-contain object-left"
        />
      )}
      <span className="text-sm font-semibold text-slate-900">{marka.label}</span>
      <span className="text-xs text-slate-400">{marka.sub}</span>
      <span className="ml-auto text-[11px] text-slate-500">
        {sag || `${donemEtiketi} döneminde yayınlanan · ${tl(marka.total, 0)} kreatif`}
      </span>
    </div>
  );
}



/** Sabit yükseklikli kutulara sığsın diye metni JS'te kısaltıyoruz:
 *  html2canvas `line-clamp`/`text-overflow` uygulamıyor, taşan satır
 *  alttaki yazının üstüne biniyor. */
const kisalt = (t, n) => {
  const m = String(t || '');
  return m.length > n ? `${m.slice(0, n - 1)}…` : m;
};

/** PDF sayfalarının üst şeridi: hangi grup, hangi ay, veri hangi anın. */
function PdfBaslik({ veri, seciliAy, oncekiAy, sayfa, toplamSayfa }) {
  const a = ayBilgisi(seciliAy);
  const damga = new Date(veri.fetchedAt || Date.now()).toLocaleString('tr-TR', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
  return (
    <div className="flex items-end justify-between border-b-2 border-slate-900 pb-2">
      <div>
        <div className="text-lg font-bold text-slate-900">
          {veri.group.name} — {D.lik()} reklam özeti
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {veri.group.note} · {ayEtiketi(seciliAy)}
          {a.devamEdiyor && (
            <span className="ml-1 font-semibold text-amber-600">
              ({D.ad()} sürüyor — {a.gecenGun}/{a.toplamGun} gün)
            </span>
          )}
          {oncekiAy ? ` · kıyas: ${ayEtiketi(oncekiAy)}` : ''}
        </div>
      </div>
      <div className="text-right text-[11px] leading-tight text-slate-500">
        <div>Canlı Meta / Google Ads / TikTok verisi</div>
        <div>{damga}</div>
        <div className="font-semibold text-slate-700">
          Sayfa {sayfa}/{toplamSayfa}
        </div>
      </div>
    </div>
  );
}

/* ── Sayfa ─────────────────────────────────────────────────────────── */

export default function HupalupaGroup() {
  const [donem, setDonem] = useState('ay');
  const [veri, setVeri] = useState(null);
  const [reklamlar, setReklamlar] = useState(null);
  const [reklamYukleniyor, setReklamYukleniyor] = useState(false);
  const [reklamHatasi, setReklamHatasi] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [seciliAy, setSeciliAy] = useState(null);
  /* Dışa aktarım (`pdfIndir`/`excelIndir`) reklamları kendisi çekebiliyor;
     o çağrılar bağımlılık listesine `seciliAy` eklemeden güncel dönemi
     görsün diye ayna ref. */
  const seciliAyRef = useRef(null);
  useEffect(() => { seciliAyRef.current = seciliAy; }, [seciliAy]);

  const getir = useCallback(async (yenile = false, kip = donem) => {
    setYukleniyor(true);
    setHata(null);
    try {
      const adet = kip === 'hafta' ? HAFTA_SAYISI : AY_SAYISI;
      const res = await fetch(
        `${API_BASE}/api/group/hupalupa/monthly?period=${kip}&count=${adet}${yenile ? '&refresh=1' : ''}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include',
        }
      );
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      donemAyarla(json);
      setVeri(json);
      setSeciliAy((onceki) =>
        onceki && json.months.includes(onceki) ? onceki : json.months[json.months.length - 1]
      );
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [donem]);

  useEffect(() => { getir(false); }, [getir]);

  /* Liste "şu an yayında olanlar"dır ama kartların altındaki SONUÇLAR seçili
     döneme aittir → kip/dönem değişince yeniden çekilir. Uca dönem geçilmezse
     backend en son döneme düşer ve ızgarada Temmuz seçiliyken kartta Ağustos
     rakamı yazardı. */
  const reklamlariGetir = useCallback(async (yenile = false, ay = null) => {
    const hedef = ay || seciliAyRef.current;
    setReklamYukleniyor(true);
    setReklamHatasi(null);
    try {
      const qs = new URLSearchParams({ period: donem });
      if (hedef) qs.set('donem', hedef);
      if (yenile) qs.set('refresh', '1');
      const res = await fetch(`${API_BASE}/api/group/hupalupa/ads?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setReklamlar(json);
      return json;
    } catch (e) {
      setReklamHatasi(e.message);
      return null;
    } finally {
      setReklamYukleniyor(false);
    }
  }, [donem]);

  /* Seçili dönem hazır olmadan çekmeye başlama: backend en son döneme düşer,
     sonra doğru dönem gelince ikinci kez çekilirdi (7 sn'lik çağrı, boşuna). */
  useEffect(() => {
    if (!seciliAy) return;
    reklamlariGetir(false, seciliAy);
  }, [reklamlariGetir, seciliAy]);

  /* ── Yapay zekâ yorumu ────────────────────────────────────────────
     Dönem BAŞINA ayrı tutulur (`${kip}:${dönem}`): yorum seçili dönemin
     rakamlarına ait, ay değişince eskisini göstermek yanlış olur.
     🔴 Kendiliğinden ÜRETİLMEZ — kullanıcı "Yorumla"ya basar. Her sayfa
     açılışında 10-20 sn'lik model çağrısı başlatmanın anlamı yok. Tek
     istisna dışa aktarım: dosyanın yorum sayfası boş kalmasın diye orada
     yoksa bir kez üretilir. */
  const [yorumlar, setYorumlar] = useState({});
  const [yorumYukleniyor, setYorumYukleniyor] = useState(false);
  const [yorumHatasi, setYorumHatasi] = useState(null);
  // Efektin `yorumlar`a bağlanıp kendini tetiklememesi için ayna ref.
  const yorumlarRef = useRef({});

  const yorumGetir = useCallback(async (ay, yenile = false) => {
    if (!ay) return null;
    const anahtar = `${donem}:${ay}`;
    if (!yenile && yorumlarRef.current[anahtar]) return yorumlarRef.current[anahtar];
    setYorumYukleniyor(true);
    setYorumHatasi(null);
    try {
      const adet = donem === 'hafta' ? HAFTA_SAYISI : AY_SAYISI;
      const res = await fetch(
        `${API_BASE}/api/group/hupalupa/yorum?period=${donem}&count=${adet}` +
          `&donem=${encodeURIComponent(ay)}${yenile ? '&refresh=1' : ''}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include',
        }
      );
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      yorumlarRef.current = { ...yorumlarRef.current, [anahtar]: json };
      setYorumlar(yorumlarRef.current);
      return json;
    } catch (e) {
      setYorumHatasi(e.message);
      return null;
    } finally {
      setYorumYukleniyor(false);
    }
  }, [donem]);

  /** Rakamlar tazelendiğinde eldeki yorum o rakamlara ait değil — atılır. */
  const yorumlariUnut = useCallback(() => {
    yorumlarRef.current = {};
    setYorumlar({});
    setYorumHatasi(null);
  }, []);

  /** Aylık ⇄ haftalık: veri kipe göre yeniden çekilir (önbellek ayrı). */
  const kipDegistir = useCallback((kip) => {
    if (kip === donem) return;
    setDonem(kip);
    setVeri(null);
    setSeciliAy(null);
    yorumlariUnut();
  }, [donem, yorumlariUnut]);

  const oncekiAy = useMemo(() => {
    if (!veri || !seciliAy) return null;
    const i = veri.months.indexOf(seciliAy);
    return i > 0 ? veri.months[i - 1] : null;
  }, [veri, seciliAy]);

  /* Reklam pivotunun grup başlıklarındaki mecra harcaması IZGARADAN gelir.
     🔑🔑 Kreatif satırlarını toplayarak hesaplanamaz: Google'ın PMax/kampanya
     varlık satırları aynı harcamayı tekrar tekrar taşıyor, toplamı gerçeğin
     2-5 katı çıkar. Marka kartındaki mecra kartıyla aynı kaynak, aynı rakam. */
  const mecraToplamlari = useMemo(() => {
    if (!veri || !seciliAy) return null;
    const out = {};
    for (const b of veri.brands || []) {
      const p = b.months?.[seciliAy]?.platforms || {};
      out[b.id] = Object.fromEntries(MECRALAR.map((mc) => [mc.key, p[mc.key] || null]));
    }
    return out;
  }, [veri, seciliAy]);

  /* ── Dışa aktarım ──────────────────────────────────────────────────
     Menü üç çıktı verir: biçimli Excel (asıl olan), sunum/PDF ve ham CSV.
     Ortak kural: her dosyada hangi ayın, hangi anın, hangi kaynağın
     rakamı olduğu ve kısmi ay uyarısı YAZAR. Rakamı paneli görmeden
     okuyan kişi de aynı sonuca varabilsin diye. */

  const [menuAcik, setMenuAcik] = useState(false);
  const [pdfHazirlaniyor, setPdfHazirlaniyor] = useState(false);
  const [pdfKreatifleri, setPdfKreatifleri] = useState(null);
  /* Dosya 20+ sayfa olabiliyor (bütün kreatifler kağıda giriyor) —
     düğme boş boş "Hazırlanıyor…" demesin, nerede olduğunu yazsın. */
  const [pdfIlerleme, setPdfIlerleme] = useState('');
  const menuRef = useRef(null);
  const pdfKapRef = useRef(null);

  useEffect(() => {
    if (!menuAcik) return undefined;
    const disariTikla = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuAcik(false);
    };
    const kacisTusu = (e) => {
      if (e.key === 'Escape') setMenuAcik(false);
    };
    document.addEventListener('mousedown', disariTikla);
    document.addEventListener('keydown', kacisTusu);
    return () => {
      document.removeEventListener('mousedown', disariTikla);
      document.removeEventListener('keydown', kacisTusu);
    };
  }, [menuAcik]);

  const dosyaAdi = veri
    ? `hupalupa-grubu-${donem === 'hafta' ? 'haftalik' : 'aylik'}-${veri.months[0]}_${
        veri.months[veri.months.length - 1]
      }`
    : 'hupalupa-grubu';

  /**
   * Excel — dört sayfa: Özet · Marka × Mecra × Ay · Marka KPI detayı ·
   * Nasıl okunur. Eski CSV yalnızca harcama sütunuydu; burada her markanın
   * KENDİ KPI'ı, mecra kırılımı, oranlar ve okuma notları da dosyada.
   *
   * 🔴 Ölçülmeyen değer 0 değil "—" yazılır (`xn`). Aksi halde "bu markada
   * dönüşüm ölçülmüyor" ile "sıfır dönüşüm aldı" Excel'de aynı görünür.
   */
  /* `opts.dondur` ile dosyayı indirmek yerine base64 döndürür (mail eki).
     🔴 Düğmeye `onClick={excelIndir}` diye bağlı: ilk argüman React olayı
     olabilir, o yüzden konumlu boolean DEĞİL nesne alanı okunuyor. */
  const excelIndir = useCallback(async (opts) => {
    const dondur = opts?.dondur === true;
    if (!veri || !seciliAy) return null;
    setMenuAcik(false);
    setPdfHazirlaniyor(true);
    try {
    // Reklam listesi henüz gelmediyse dosyada o sayfa eksik kalmasın.
    const reklamVerisi = reklamlar?.brands?.length ? reklamlar : await reklamlariGetir(false);
    /* 🔴 Yorum BURADA ÜRETİLMEZ, yalnızca varsa kullanılır. Üretmek Gemini'ye
       gitmek demek: kullanıcının istemediği bir çağrı, dışa aktarımı (ve mail
       gönderimini) dakikalarca bekletiyordu. Kural aynı — model yalnızca
       "Yorumla" düğmesiyle çalışır. Yorum yoksa dosyada o sayfa açılmaz;
       menü zaten "dört sayfa / beş sayfa" diye buna göre yazıyor. */
    const yorumVerisi = yorumlarRef.current[`${donem}:${seciliAy}`] || null;
    const aylar = veri.months;
    const damga = new Date(veri.fetchedAt || Date.now()).toLocaleString('tr-TR', {
      dateStyle: 'long',
      timeStyle: 'short',
    });
    const ayD = ayBilgisi(seciliAy);
    const kiyasTuru = ayD.devamEdiyor ? 'günlük ortalama' : `${D.lik()} toplam`;
    const metin = (b) => KPI_METIN[b.kpi] || KPI_METIN.reach;
    const oran = (pay, payda, carpan = 1) =>
      payda > 0 ? (pay / payda) * carpan : null;
    const wb = XLSX.utils.book_new();

    /* ── 1) Özet ─────────────────────────────────────────────────── */
    const OZ = 16;
    const s1 = [];
    const s1m = [];
    const s1tam = () => s1m.push({ s: { r: s1.length - 1, c: 0 }, e: { r: s1.length - 1, c: OZ - 1 } });

    s1.push([xt(`${veri.group.name} — ${D.lik()} reklam özeti`, XL_STIL.baslik)]); s1tam();
    s1.push([
      xt(
        `Dönem: ${ayBasligiTam(seciliAy)}${oncekiAy ? `   ·   kıyas dönemi: ${ayBasligiTam(oncekiAy)}` : ''}`,
        XL_STIL.altBaslik
      ),
    ]); s1tam();
    s1.push([
      xt(
        `${veri.group.note} · rakamlar canlı Meta / Google Ads / TikTok hesaplarından ${damga} itibarıyla okundu`,
        XL_STIL.altBaslik
      ),
    ]); s1tam();
    if (ayD.devamEdiyor) {
      s1.push([
        xt(
          `DİKKAT: ${ayEtiketi(seciliAy)} henüz bitmedi — ${ayD.gecenGun}/${ayD.toplamGun} gün. ` +
            `Dolu ${D.ad()}larla doğrudan kıyaslamayın; aşağıdaki değişim yüzdeleri günlük ortalamaya göre hesaplandı.`,
          XL_STIL.uyari
        ),
      ]); s1tam();
    }
    s1.push([]);

    const t = veri.totals[seciliAy];
    const o = oncekiAy ? veri.totals[oncekiAy] : null;

    s1.push(xlBolumSatiri('GRUP TOPLAMI', OZ)); s1tam();
    s1.push([
      xt('Metrik', XL_STIL.th),
      xt(ayBasligiKisa(seciliAy), XL_STIL.th),
      xt(oncekiAy ? ayBasligiKisa(oncekiAy) : 'önceki ay yok', XL_STIL.th),
      xt('Değişim', XL_STIL.th),
      xt('Kıyas türü', XL_STIL.th),
    ]);
    /** Kısmi ayda toplamları değil günlük ortalamayı kıyaslar (`gunluk`). */
    const metrikSatiri = (ad, simdi, once, z, gunluk = true, stil = XL_STIL.sayi) => {
      const k =
        once == null
          ? { yuzde: null }
          : gunluk
          ? kiyasla(simdi, once, seciliAy, oncekiAy)
          : { yuzde: degisim(simdi, once) };
      return [
        xt(ad),
        xn(simdi, z, stil),
        xn(once, z),
        xn(k.yuzde, XL_BICIM.yuzde),
        xt(gunluk ? kiyasTuru : 'oran — dönem uzunluğundan etkilenmez', XL_STIL.metinSoluk),
      ];
    };
    s1.push(metrikSatiri('Spend', t.spend, o?.spend, XL_BICIM.para));
    s1.push(metrikSatiri('Impressions', t.impressions, o?.impressions, XL_BICIM.adet));
    s1.push(metrikSatiri('Clicks', t.clicks, o?.clicks, XL_BICIM.adet));
    s1.push(
      metrikSatiri(
        'CPM — bin gösterim maliyeti',
        oran(t.spend, t.impressions, 1000),
        o ? oran(o.spend, o.impressions, 1000) : null,
        XL_BICIM.para2,
        false
      )
    );
    s1.push(
      metrikSatiri(
        'TBM — tıklama başına maliyet',
        oran(t.spend, t.clicks),
        o ? oran(o.spend, o.clicks) : null,
        XL_BICIM.para2,
        false
      )
    );
    s1.push(
      metrikSatiri(
        'Ciro — sitede ölçülen, GA4 (yalnızca HUPALUPA Store)',
        t.revenue || null,
        o?.revenue || null,
        XL_BICIM.para,
        true,
        XL_STIL.ciro
      )
    );
    if (ayD.devamEdiyor) {
      const oD = oncekiAy ? ayBilgisi(oncekiAy) : null;
      s1.push([
        xt('Günlük ortalama harcama'),
        xn(t.spend / ayD.gecenGun, XL_BICIM.para),
        xn(o && oD ? o.spend / oD.gecenGun : null, XL_BICIM.para),
        xn(o && oD ? degisim(t.spend / ayD.gecenGun, o.spend / oD.gecenGun) : null, XL_BICIM.yuzde),
        xt('günlük ortalama', XL_STIL.metinSoluk),
      ]);
      s1.push([
        xt(`Bu hızla ${ayEtiketi(seciliAy)} sonu tahmini`),
        xn(aySonuTahmini(t.spend, seciliAy), XL_BICIM.para),
        xn(o?.spend, XL_BICIM.para),
        xn(o ? degisim(aySonuTahmini(t.spend, seciliAy), o.spend) : null, XL_BICIM.yuzde),
        xt('tahmin — gerçekleşme değil', XL_STIL.metinSoluk),
      ]);
    }
    s1.push([]);

    s1.push(xlBolumSatiri(`MECRA DAĞILIMI — ${ayBasligiTam(seciliAy)}`, OZ)); s1tam();
    s1.push([
      xt('Mecra', XL_STIL.th),
      xt('Spend', XL_STIL.th),
      xt('Pay', XL_STIL.th),
      xt(oncekiAy ? `${ayEtiketi(oncekiAy, true)} harcama` : 'önceki ay yok', XL_STIL.th),
    ]);
    for (const mc of MECRALAR) {
      const v = t.platforms?.[mc.key] || 0;
      if (!v && !(o?.platforms?.[mc.key])) continue;
      s1.push([
        xt(mc.label),
        xn(v || null, XL_BICIM.para),
        xn(oran(v, t.spend, 100), XL_BICIM.yuzde),
        xn(o?.platforms?.[mc.key] || null, XL_BICIM.para),
      ]);
    }
    s1.push([
      xt('TOPLAM', XL_STIL.metinKalin),
      xn(t.spend, XL_BICIM.para, XL_STIL.sayiKalin),
      xn(t.spend ? 100 : null, XL_BICIM.yuzde, XL_STIL.sayiKalin),
      xn(o?.spend, XL_BICIM.para, XL_STIL.sayiKalin),
    ]);
    s1.push([]);

    s1.push(xlBolumSatiri(`MARKA ÖZETİ — ${ayBasligiTam(seciliAy)}`, OZ)); s1tam();
    s1.push(
      [
        'Marka', 'İş kolu', 'Bu markada ne ölçülüyor', 'Spend', 'Google', 'Meta', 'TikTok',
        'Impressions', 'Clicks', 'CPM', 'CPC', 'Result', 'Cost / result',
        'Revenue (GA4 · sitede ölçülen)', 'ROAS (GA4)', 'Not',
      ].map((h) => xt(h, XL_STIL.th))
    );
    for (const b of veri.brands) {
      const m = b.months?.[seciliAy];
      if (!m) continue;
      const km = metin(b);
      s1.push([
        xt(b.label),
        xt(b.sub, XL_STIL.metinSoluk),
        xt(km.sonucAd ? `${km.sonucAd} (${b.kpi})` : 'ölçülmüyor — yalnızca erişim', XL_STIL.metinSoluk),
        xn(m.spend, XL_BICIM.para),
        xn(m.platforms?.GOOGLE_ADS?.spend || null, XL_BICIM.para),
        xn(m.platforms?.META_ADS?.spend || null, XL_BICIM.para),
        xn(m.platforms?.TIKTOK_ADS?.spend || null, XL_BICIM.para),
        xn(m.impressions, XL_BICIM.adet),
        xn(m.clicks, XL_BICIM.adet),
        xn(m.cpm, XL_BICIM.para2),
        xn(m.cpc, XL_BICIM.para2),
        xn(m.result, XL_BICIM.adet),
        xn(m.costPerResult, XL_BICIM.para),
        xn(b.kpi === 'revenue' ? m.revenue : null, XL_BICIM.para, XL_STIL.ciro),
        xn(b.kpi === 'revenue' ? m.roas : null, XL_BICIM.kat),
        xt(km.aciklama, XL_STIL.metinSoluk),
      ]);
    }
    s1.push([
      xt('GRUP TOPLAM', XL_STIL.grupMetin),
      xt('dört marka', XL_STIL.grupMetin),
      xt('KPI’lar farklı — yalnızca harcama/gösterim toplanır', XL_STIL.grupMetin),
      xn(t.spend, XL_BICIM.para, XL_STIL.grupSayi),
      xn(t.platforms?.GOOGLE_ADS || null, XL_BICIM.para, XL_STIL.grupSayi),
      xn(t.platforms?.META_ADS || null, XL_BICIM.para, XL_STIL.grupSayi),
      xn(t.platforms?.TIKTOK_ADS || null, XL_BICIM.para, XL_STIL.grupSayi),
      xn(t.impressions, XL_BICIM.adet, XL_STIL.grupSayi),
      xn(t.clicks, XL_BICIM.adet, XL_STIL.grupSayi),
      xn(oran(t.spend, t.impressions, 1000), XL_BICIM.para2, XL_STIL.grupSayi),
      xn(oran(t.spend, t.clicks), XL_BICIM.para2, XL_STIL.grupSayi),
      xt('—', XL_STIL.grupSayi),
      xt('—', XL_STIL.grupSayi),
      xn(t.revenue || null, XL_BICIM.para, XL_STIL.grupSayi),
      xt('—', XL_STIL.grupSayi),
      xt('Sonuç ve ROAS toplanmaz — bkz. "Nasıl okunur"', XL_STIL.grupMetin),
    ]);

    XLSX.utils.book_append_sheet(
      wb,
      xlSayfa(
        s1,
        [
          { wch: 26 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 14 }, { wch: 14 },
          { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 11 }, { wch: 13 },
          { wch: 20 }, { wch: 24 }, { wch: 12 }, { wch: 52 },
        ],
        s1m
      ),
      'Özet'
    );

    /* ── 1b) Yapay zekâ yorumu ───────────────────────────────────
       Panelde okunan yorumun aynısı. Rakam İÇERMEZ diye ayrı sayfa:
       metin sütunu geniş, hücreler sarmalı — özet sayfasının ızgarasına
       sıkıştırılırsa okunmuyor. */
    if (yorumVerisi) {
      const sy = [];
      const sym = [];
      const SY = 3;
      const sytam = () => sym.push({ s: { r: sy.length - 1, c: 0 }, e: { r: sy.length - 1, c: SY - 1 } });

      sy.push([xt(`${veri.group.name} — yapay zekâ yorumu`, XL_STIL.baslik)]); sytam();
      sy.push([
        xt(
          `${ayBasligiTam(seciliAy)}${ayD.devamEdiyor ? ` (${D.ad()} sürüyor — ${ayD.gecenGun}/${ayD.toplamGun} gün)` : ''} · ` +
            `kıyas: ${kiyasTuru} · ${yorumVerisi.model} · ` +
            `${new Date(yorumVerisi.uretildi).toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' })}`,
          XL_STIL.altBaslik
        ),
      ]); sytam();
      sy.push([]);

      sy.push(xlBolumSatiri('ÖZET', SY)); sytam();
      sy.push([xt(yorumVerisi.ozet, XL_STIL.not)]); sytam();
      sy.push([]);

      if (yorumVerisi.bulgular?.length) {
        sy.push(xlBolumSatiri('BULGULAR', SY)); sytam();
        sy.push(['Marka', 'Bulgu', 'Açıklama'].map((h) => xt(h, XL_STIL.th)));
        for (const b of yorumVerisi.bulgular) {
          sy.push([
            xt(b.marka || 'Grup geneli', XL_STIL.metinSoluk),
            xt(b.baslik, XL_STIL.metinKalin),
            xt(b.metin, XL_STIL.not),
          ]);
        }
        sy.push([]);
      }

      if (yorumVerisi.aksiyonlar?.length) {
        sy.push(xlBolumSatiri('BU HAFTA YAPILACAKLAR', SY)); sytam();
        sy.push(['Marka', 'Aksiyon', 'Açıklama'].map((h) => xt(h, XL_STIL.th)));
        for (const a of yorumVerisi.aksiyonlar) {
          sy.push([
            xt(a.marka || 'Grup geneli', XL_STIL.metinSoluk),
            xt(a.baslik, XL_STIL.metinKalin),
            xt(a.metin, XL_STIL.not),
          ]);
        }
        sy.push([]);
      }

      if (yorumVerisi.soruIsaretleri?.length) {
        sy.push(xlBolumSatiri('SORU İŞARETLERİ — veri/ölçüm boşlukları', SY)); sytam();
        for (const q of yorumVerisi.soruIsaretleri) {
          sy.push([xt(q, XL_STIL.not)]); sytam();
        }
        sy.push([]);
      }

      sy.push(xlBolumSatiri('BU SAYFA NASIL ÜRETİLDİ', SY)); sytam();
      sy.push([
        xt(
          'Metni yapay zekâ (Gemini) yazdı; rakamları YAZMADI. Değişim yüzdeleri, günlük ' +
            'ortalamalar ve mecra payları panelin kendi hesabından hazır verildi, modelin yeni ' +
            'sayı üretmesi engellendi. Yine de karar öncesi rakamı bu dosyanın Özet sayfasından doğrulayın.',
          XL_STIL.not
        ),
      ]); sytam();
      sy.push([xt(`Veri anı: ${damga} · doğrudan mecra API'lerinden.`, XL_STIL.not)]); sytam();

      XLSX.utils.book_append_sheet(
        wb,
        xlSayfa(sy, [{ wch: 26 }, { wch: 34 }, { wch: 96 }], sym),
        'Yapay zekâ yorumu'
      );
    }

    /* ── 2) Marka × Mecra × Ay ───────────────────────────────────── */
    const s2 = [];
    const s2m = [];
    const S2 = aylar.length + 2;
    const s2tam = () => s2m.push({ s: { r: s2.length - 1, c: 0 }, e: { r: s2.length - 1, c: S2 - 1 } });

    s2.push([
      xt(`${veri.group.name} — marka × mecra × ${D.ad()}: harcama ve ciro`, XL_STIL.baslik),
    ]); s2tam();
    s2.push([
      xt(
        `${ayEtiketi(aylar[0])} – ${ayEtiketi(aylar[aylar.length - 1])} · ${damga} itibarıyla canlı okundu · tutarlar TL`,
        XL_STIL.altBaslik
      ),
    ]); s2tam();
    s2.push([]);
    s2.push([
      xt('Marka / mecra', XL_STIL.th),
      ...aylar.map((m) => xt(ayBasligiKisa(m), XL_STIL.th)),
      xt(`${aylar.length} ${D.ad()} toplamı`, XL_STIL.th),
    ]);

    for (const b of veri.brands) {
      const bToplam = aylar.reduce((x, m) => x + (b.months[m]?.spend || 0), 0);
      s2.push([
        xt(b.label, XL_STIL.metinKalin),
        ...aylar.map((m) => xn(b.months[m]?.spend || null, XL_BICIM.para, XL_STIL.sayiKalin)),
        xn(bToplam || null, XL_BICIM.para, XL_STIL.sayiKalin),
      ]);
      for (const mc of MECRALAR) {
        const varMi = aylar.some((m) => b.months[m]?.platforms?.[mc.key]?.spend);
        if (!varMi) continue;
        const mcToplam = aylar.reduce((x, m) => x + (b.months[m]?.platforms?.[mc.key]?.spend || 0), 0);
        s2.push([
          xt(`     ${mc.label}`, XL_STIL.metinSoluk),
          ...aylar.map((m) => xn(b.months[m]?.platforms?.[mc.key]?.spend || null, XL_BICIM.para, XL_STIL.sayiSoluk)),
          xn(mcToplam || null, XL_BICIM.para, XL_STIL.sayiSoluk),
        ]);
      }

      /* 🔴 Aşağıdaki satırlar HARCAMA DEĞİL CİRO. Ekrandaki ızgarada da aynı
         yerde duruyorlar; etiketlerinde "ciro" yazıyor ve tablo altındaki not
         bunu açıklıyor. Yalnızca GA4'ü olan markada açılır. */
      const bGa4 = aylar.some((m) => b.months?.[m]?.ga4Revenue != null);
      if (bGa4) {
        const topla = (sec) => aylar.reduce((x, m) => x + (sec(b.months[m] || {}) || 0), 0);
        s2.push([
          xt('     Revenue (GA4 · sitede ölçülen)', XL_STIL.metinKalin),
          ...aylar.map((m) => xn(b.months[m]?.revenue || null, XL_BICIM.para, XL_STIL.ciro)),
          xn(topla((d) => d.revenue) || null, XL_BICIM.para, XL_STIL.ciro),
        ]);
        s2.push([
          xt('     Sipariş (GA4)', XL_STIL.metinSoluk),
          ...aylar.map((m) => xn(b.months[m]?.ga4Orders ?? null, XL_BICIM.adet, XL_STIL.sayiSoluk)),
          xn(topla((d) => d.ga4Orders) || null, XL_BICIM.adet, XL_STIL.sayiSoluk),
        ]);
      }
    }
    s2.push([
      xt('GRUP TOPLAM', XL_STIL.grupMetin),
      ...aylar.map((m) => xn(veri.totals[m]?.spend || null, XL_BICIM.para, XL_STIL.grupSayi)),
      xn(aylar.reduce((x, m) => x + (veri.totals[m]?.spend || 0), 0) || null, XL_BICIM.para, XL_STIL.grupSayi),
    ]);
    s2.push([]);
    const yarimAy = aylar.filter((m) => ayBilgisi(m).devamEdiyor);
    s2.push([
      xt(
        yarimAy.length
          ? `Başlığında "${ayBasligiKisa(yarimAy[0]).split(' · ')[1]}" yazan ${D.ad()} henüz bitmemiştir; o sütun dolu ${D.ad()}larla doğrudan kıyaslanmaz.`
          : `Sütunların tamamı bitmiş ${D.ad()}lardır.`,
        XL_STIL.not
      ),
    ]); s2tam();
    s2.push([
      xt(
        'Ciro ve sipariş satırları harcama DEĞİLDİR; sitenin kendi ölçümünden (GA4) gelir. ' +
          'Mecraların beyan ettiği ciro kullanılmaz — aynı satışı Google da Meta da kendine yazdığı ' +
          'için şişiktir. GRUP TOPLAM satırı yalnızca harcamadır.',
        XL_STIL.not
      ),
    ]); s2tam();

    XLSX.utils.book_append_sheet(
      wb,
      xlSayfa(s2, [{ wch: 30 }, ...aylar.map(() => ({ wch: 16 })), { wch: 18 }], s2m),
      `Marka × Mecra × ${D.Ad()}`
    );

    /* ── 3) Marka KPI detayı ─────────────────────────────────────── */
    const s3 = [];
    const s3m = [];
    const S3 = 17;  // +2 Engagement/rate, +1 Views
    const s3tam = () => s3m.push({ s: { r: s3.length - 1, c: 0 }, e: { r: s3.length - 1, c: S3 - 1 } });

    s3.push([xt(`${veri.group.name} — marka bazında ${D.lik()} detay`, XL_STIL.baslik)]); s3tam();
    s3.push([
      xt(
        'Her marka KENDİ KPI’ıyla gösterilir; sonuç sütununun başlığı markadan markaya değişir. ' +
          'Ciro, sipariş ve ROAS yalnızca satış ölçülen markada (HUPALUPA Store) doludur ve ' +
          'sitenin kendi ölçümünden (GA4) gelir.',
        XL_STIL.altBaslik
      ),
    ]); s3tam();

    for (const b of veri.brands) {
      const km = metin(b);
      s3.push([]);
      s3.push(xlBolumSatiri(`${b.label} — ${b.sub} · ${km.aciklama}`, S3)); s3tam();
      const ga4Var = aylar.some((m) => b.months?.[m]?.ga4Revenue != null);
      s3.push(
        [
          D.Ad(), 'Spend', 'Google', 'Meta', 'TikTok', 'Impressions', 'Views', 'Clicks', 'CTR',
          'CPM', 'CPC', 'Engagement', 'Engagement rate',
          km.sonucAd || 'Result', km.birimAd || 'Cost / result',
          ga4Var ? 'Revenue (GA4 · sitede ölçülen)' : 'Revenue', ga4Var ? 'ROAS (GA4)' : 'ROAS',
        ].map((h) => xt(h, XL_STIL.th))
      );
      const topla = (sec) => aylar.reduce((x, m) => x + (sec(b.months[m] || {}) || 0), 0);
      for (const m of aylar) {
        const d = b.months[m] || {};
        s3.push([
          xt(ayBasligiTam(m), m === seciliAy ? XL_STIL.metinKalin : XL_STIL.metin),
          xn(d.spend || null, XL_BICIM.para),
          xn(d.platforms?.GOOGLE_ADS?.spend || null, XL_BICIM.para),
          xn(d.platforms?.META_ADS?.spend || null, XL_BICIM.para),
          xn(d.platforms?.TIKTOK_ADS?.spend || null, XL_BICIM.para),
          xn(d.impressions || null, XL_BICIM.adet),
          xn(d.views || null, XL_BICIM.adet),
          xn(d.clicks || null, XL_BICIM.adet),
          xn(oran(d.clicks, d.impressions, 100), XL_BICIM.yuzde),
          xn(d.cpm, XL_BICIM.para2),
          xn(d.cpc, XL_BICIM.para2),
          /* 🔴 Google'da etkileşim ölçülmez → boş geçer, 0 yazılmaz. */
          xn(d.engagement ?? null, XL_BICIM.adet),
          xn(d.engagementRate ?? null, XL_BICIM.yuzde),
          xn(km.sonucAd ? d.result : null, XL_BICIM.adet),
          xn(km.sonucAd ? d.costPerResult : null, XL_BICIM.para),
          xn(b.kpi === 'revenue' ? d.revenue : null, XL_BICIM.para, XL_STIL.ciro),
          xn(b.kpi === 'revenue' ? d.roas : null, XL_BICIM.kat),
        ]);
      }
      const tHarcama = topla((d) => d.spend);
      const tGosterim = topla((d) => d.impressions);
      const tTiklama = topla((d) => d.clicks);
      const tIzlenme = topla((d) => d.views);
      const tSonuc = topla((d) => d.result);
      // Etkileşim hiç ölçülmüyorsa toplam da BOŞ kalmalı (0 değil).
      const etkVar = aylar.some((m) => b.months?.[m]?.engagement != null);
      const tEtkilesim = etkVar ? topla((d) => d.engagement) : null;
      const tCiro = topla((d) => d.revenue);
      s3.push([
        xt(`${aylar.length} ${D.ad()} toplamı`, XL_STIL.metinKalin),
        xn(tHarcama || null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(topla((d) => d.platforms?.GOOGLE_ADS?.spend) || null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(topla((d) => d.platforms?.META_ADS?.spend) || null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(topla((d) => d.platforms?.TIKTOK_ADS?.spend) || null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(tGosterim || null, XL_BICIM.adet, XL_STIL.sayiKalin),
        xn(tIzlenme || null, XL_BICIM.adet, XL_STIL.sayiKalin),
        xn(tTiklama || null, XL_BICIM.adet, XL_STIL.sayiKalin),
        xn(oran(tTiklama, tGosterim, 100), XL_BICIM.yuzde, XL_STIL.sayiKalin),
        xn(oran(tHarcama, tGosterim, 1000), XL_BICIM.para2, XL_STIL.sayiKalin),
        xn(oran(tHarcama, tTiklama), XL_BICIM.para2, XL_STIL.sayiKalin),
        xn(tEtkilesim, XL_BICIM.adet, XL_STIL.sayiKalin),
        xn(tEtkilesim == null ? null : oran(tEtkilesim, tGosterim, 100), XL_BICIM.yuzde, XL_STIL.sayiKalin),
        xn(km.sonucAd ? tSonuc || null : null, XL_BICIM.adet, XL_STIL.sayiKalin),
        xn(km.sonucAd ? oran(tHarcama, tSonuc) : null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(b.kpi === 'revenue' ? tCiro || null : null, XL_BICIM.para, XL_STIL.sayiKalin),
        xn(b.kpi === 'revenue' ? oran(tCiro, tHarcama) : null, XL_BICIM.kat, XL_STIL.sayiKalin),
      ]);
    }

    XLSX.utils.book_append_sheet(
      wb,
      xlSayfa(
        s3,
        [
          { wch: 22 }, { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
          { wch: 12 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 11 },
          { wch: 13 }, { wch: 16 },
          { wch: 14 }, { wch: 18 }, { wch: 24 }, { wch: 12 },
        ],
        s3m
      ),
      'Marka KPI detayı'
    );

    /* ── 4) Nasıl okunur ─────────────────────────────────────────── */
    const s4 = [];
    const s4m = [];
    const s4r = [];
    const S4 = 8;
    const s4tam = (hpt) => {
      s4m.push({ s: { r: s4.length - 1, c: 0 }, e: { r: s4.length - 1, c: S4 - 1 } });
      s4r[s4.length - 1] = { hpt: hpt || 16 };
    };
    s4.push([xt('Bu dosya nasıl okunur', XL_STIL.baslik)]); s4tam(22);
    s4.push([xt(`${veri.group.name} · ${veri.group.note}`, XL_STIL.altBaslik)]); s4tam();
    s4.push([]);
    for (const n of okumaNotlari(aylar)) {
      s4.push([xt(n.baslik, XL_STIL.notBaslik)]); s4tam();
      s4.push([xt(n.metin, XL_STIL.not)]); s4tam(Math.ceil(n.metin.length / 105) * 15 + 6);
      s4.push([]);
    }
    s4.push(xlBolumSatiri('MARKA BAŞINA KPI', S4)); s4tam();
    s4.push(
      ['Marka', 'İş kolu', 'Ölçülen sonuç', 'Ne demek'].map((h) => xt(h, XL_STIL.th))
    );
    for (const b of veri.brands) {
      const km = metin(b);
      s4.push([
        xt(b.label),
        xt(b.sub, XL_STIL.metinSoluk),
        xt(km.sonucAd || 'ölçülmüyor', XL_STIL.metin),
        xt(km.aciklama, XL_STIL.metinSoluk),
      ]);
    }
    s4.push([]);
    s4.push(xlBolumSatiri('KAYNAK', S4)); s4tam();
    s4.push([xt(`Veri anı: ${damga} · doğrudan mecra API'lerinden, veritabanından değil.`, XL_STIL.not)]); s4tam();
    s4.push([xt(`Dönem: ${ayEtiketi(aylar[0])} – ${ayEtiketi(aylar[aylar.length - 1])} · para birimi TL.`, XL_STIL.not)]); s4tam();

    const hatalar = veri.brands.flatMap((b) => (b.errors || []).map((e) => ({ marka: b.label, ...e })));
    if (hatalar.length) {
      s4.push([]);
      s4.push(xlBolumSatiri('OKUNAMAYAN HESAPLAR — bu dosyadaki toplamlar eksik olabilir', S4)); s4tam();
      s4.push(['Marka', 'Mecra', 'Hesap', 'Hata'].map((h) => xt(h, XL_STIL.th)));
      for (const h of hatalar) {
        s4.push([xt(h.marka), xt(h.platform, XL_STIL.metinSoluk), xt(h.account, XL_STIL.metinSoluk), xt(h.error, XL_STIL.metinSoluk)]);
      }
    }

    XLSX.utils.book_append_sheet(
      wb,
      xlSayfa(
        s4,
        [{ wch: 26 }, { wch: 22 }, { wch: 20 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }],
        s4m,
        s4r
      ),
      'Nasıl okunur'
    );

    /* ── 5) Dönemde yayınlanan reklamlar ─────────────────────────
       Excel'e görsel gömülemediği için her satırda kreatifin ve reklamın
       BAĞLANTISI var: "Görseli aç" tıklanınca kare açılır. Aynı liste PDF'te
       de tam basılır; buradaki fark tıklanabilir bağlantılar. */
    if (reklamVerisi?.brands?.length) {
      const s5 = [];
      const s5m = [];
      const S5 = 8;
      const s5tam = () => s5m.push({ s: { r: s5.length - 1, c: 0 }, e: { r: s5.length - 1, c: S5 - 1 } });
      const bag = (metin, url) =>
        url
          ? { v: metin, t: 's', l: { Target: url }, s: { ...XL_STIL.metin, font: { color: { rgb: '1D4ED8' }, underline: true } } }
          : xt('—', XL_STIL.metinSoluk);

      s5.push([xt(`${veri.group.name} — ${ayEtiketi(seciliAy)} döneminde yayınlanan reklamlar`, XL_STIL.baslik)]); s5tam();
      s5.push([
        xt(
          `${tl(reklamVerisi.total, 0)} kreatif · mecra hesaplarından ${new Date(
            reklamVerisi.fetchedAt || Date.now()
          ).toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' })} itibarıyla canlı okundu`,
          XL_STIL.altBaslik
        ),
      ]); s5tam();
      s5.push([
        xt(
          'Bu dönemde GÖSTERİM ALMIŞ bütün kreatifler listelenir — bugün duraklatılmış olanlar ' +
            'da dahil ("Durum" sütunu). Liste mecranın o dönemdeki teslimatından üretilir, ' +
            'bugünkü açık/kapalı durumundan değil; böylece yukarıdaki harcama tablosuyla aynı ' +
            'dönemi anlatır. Görsel ve reklam bağlantıları tıklanabilir — Meta bağlantısı ' +
            'panele giriş yapmış tarayıcıda açılır.',
          XL_STIL.not
        ),
      ]); s5tam();
      s5.push([
        xt(
          `Harcama, gösterim, tık ve sonuç sütunları ${ayEtiketi(seciliAy)} dönemine aittir. ` +
            'Sonuç markanın kendi KPI\'sıdır (Store satış, Eğlence mesaj, Kixx form, Keramet erişim); ' +
            'kreatif seviyesinde site ölçümü (GA4) olmadığı için Store\'daki satış mecranın beyanıdır.',
          XL_STIL.not
        ),
      ]); s5tam();
      s5.push([
        xt(
          'Etkileşim = reaksiyon + yorum + paylaşım + kayıt (GERÇEK etkileşim). Mecranın ' +
            '"gönderi etkileşimi" diye yazdığı sayı KULLANILMAZ: Ağu 2026\'da Eğlence\'de o ' +
            'sayı 1.780.248 iken gerçek etkileşim 5.192\'ydi, aradaki farkın tamamı 3 saniyelik ' +
            'video izlenmesi. Oran gösterime bölünerek hesaplanır. Google\'da etkileşim ' +
            'ÖLÇÜLMEZ (karşılığı yok) — o satırlarda boş, sıfır değil.',
          XL_STIL.not
        ),
      ]); s5tam();
      s5.push([
        xt(
          'DİKKAT — "Ölçüm" sütunu "TOPLANAMAZ" yazan satırların harcaması o kreatifin payı DEĞİL, ' +
            'göründüğü reklamın rakamıdır (Google PMax/kampanya varlıkları: tek gösterimde birkaç ' +
            'görsel çıktığı için aynı harcama her varlığa tekrar yazılır). Bu satırların toplamı ' +
            'gerçeğin 2-5 katı çıkar; sütun toplamı ALMAYIN. Gerçek harcama için panelin ızgarasına bakın.',
          XL_STIL.uyari
        ),
      ]); s5tam();
      s5.push([]);

      // Marka × mecra sayım özeti
      s5.push(xlBolumSatiri('ÖZET — MARKA × MECRA', S5)); s5tam();
      s5.push(
        ['Marka', ...MECRALAR.map((m) => m.label), 'Toplam'].map((h) => xt(h, XL_STIL.th))
      );
      for (const b of reklamVerisi.brands) {
        s5.push([
          xt(b.label),
          ...MECRALAR.map((m) => xn(b.platforms?.[m.key]?.ads?.length || null, XL_BICIM.adet)),
          xn(b.total || null, XL_BICIM.adet, XL_STIL.sayiKalin),
        ]);
      }
      s5.push([
        xt('TOPLAM', XL_STIL.grupMetin),
        ...MECRALAR.map((m) =>
          xn(
            reklamVerisi.brands.reduce((n, b) => n + (b.platforms?.[m.key]?.ads?.length || 0), 0) || null,
            XL_BICIM.adet,
            XL_STIL.grupSayi
          )
        ),
        xn(reklamVerisi.total || null, XL_BICIM.adet, XL_STIL.grupSayi),
      ]);

      // Marka marka tam liste
      for (const b of reklamVerisi.brands) {
        s5.push([]);
        s5.push(xlBolumSatiri(`${b.label} — ${tl(b.total, 0)} kreatif yayınlandı`, S5)); s5tam();
        if (!b.total) {
          s5.push([xt('Bu markada bu dönemde reklam yayınlanmamış.', XL_STIL.not)]); s5tam();
          continue;
        }
        s5.push(
          ['Mecra', 'Tip', 'Reklam / kreatif', 'Kampanya', 'Reklam grubu', 'Durum',
           'Spend', 'Impressions', 'Clicks', 'CTR', 'Engagement', 'Engagement rate',
           'Engagement campaign', 'Result', 'Result type', 'Ölçüm',
           'Görsel', 'Reklamı aç', 'Kreatif id']
            .map((h) => xt(h, XL_STIL.th))
        );
        /* 🔑 Ekrandaki tabloyla AYNI sıra: mecralar tek listede, harcamaya
           göre azalan. Dosyayı açan kişi panelde gördüğü sırayı bulsun. */
        const tumSatirlar = MECRALAR.flatMap((mc) =>
          (b.platforms?.[mc.key]?.ads || []).map((r) => ({ ...r, mecraAdi: mc.label }))
        ).sort((x, y) => (y.metrics?.spend || 0) - (x.metrics?.spend || 0));
        {
          for (const r of tumSatirlar) {
            const m = r.metrics;
            s5.push([
              xt(r.mecraAdi, XL_STIL.metinSoluk),
              xt((REKLAM_TIPI[r.type] || REKLAM_TIPI.metin).ad, XL_STIL.metinSoluk),
              xt(r.name || '—'),
              xt(r.campaign || '—', XL_STIL.metinSoluk),
              xt(r.adset || '—', XL_STIL.metinSoluk),
              /* `active` null ise mecra durum vermedi → "—", "durdu" DEĞİL. */
              xt(
                r.active === false ? 'durdu' : r.active === true ? 'yayında' : '—',
                XL_STIL.metinSoluk
              ),
              /* 🔑 Ölçülmeyen "0" değil BOŞ geçer; sıfır aldı ile ölçülmüyor
                 aynı görünmesin (dosyanın geri kalanındaki kural). */
              xn(m ? m.spend : null, XL_BICIM.para),
              xn(m ? m.impressions : null, XL_BICIM.adet),
              xn(m ? m.clicks : null, XL_BICIM.adet),
              xn(m ? m.ctr : null, XL_BICIM.yuzde),
              /* 🔴 Google'da etkileşim ÖLÇÜLMÜYOR → boş geçer, 0 yazılmaz. */
              xn(m && m.engagement != null ? m.engagement : null, XL_BICIM.adet),
              xn(m && m.engagementRate != null ? m.engagementRate : null, XL_BICIM.yuzde),
              xt(m && m.engagement != null ? (m.engagementCampaign ? 'evet' : 'hayır') : '—',
                 XL_STIL.metinSoluk),
              xn(m && m.result != null ? m.result : null, XL_BICIM.adet),
              xt(m?.resultLabel || '—', XL_STIL.metinSoluk),
              /* 🔴 Bu sütun ŞART: varlık satırlarının harcaması toplanamaz.
                 Excel'de sütun toplamı almak bir tık uzakta; uyarı satırda
                 durmazsa dosyayı açan kişi doğrudan yanlış toplamı görür. */
              xt(
                !m ? '—' : m.kesin ? 'kendi sonucu' : 'göründüğü reklamın toplamı · TOPLANAMAZ',
                m && !m.kesin ? XL_STIL.uyari : XL_STIL.metinSoluk
              ),
              bag(kreatifGorsel(r) ? 'Görseli aç' : '—', kreatifGorsel(r)),
              bag(r.preview ? 'Reklamı aç' : '—', r.preview),
              xt(r.id || '—', XL_STIL.metinSoluk),
            ]);
          }
        }
        for (const h of b.errors || []) {
          s5.push([xt(`${h.platform} okunamadı: ${h.error}`, XL_STIL.uyari)]); s5tam();
        }
      }

      XLSX.utils.book_append_sheet(
        wb,
        xlSayfa(
          s5,
          [
            { wch: 12 }, { wch: 10 }, { wch: 52 }, { wch: 34 }, { wch: 28 }, { wch: 9 },
            { wch: 13 }, { wch: 12 }, { wch: 10 }, { wch: 9 },
            { wch: 11 }, { wch: 15 }, { wch: 19 },
            { wch: 10 }, { wch: 20 }, { wch: 38 },
            { wch: 13 }, { wch: 13 }, { wch: 20 },
          ],
          s5m
        ),
        'Yayınlanan reklamlar'
      );
    }

    if (dondur) {
      return {
        ad: `${dosyaAdi}.xlsx`,
        tur: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        base64: XLSX.write(wb, { type: 'base64', bookType: 'xlsx' }),
      };
    }
    XLSX.writeFile(wb, `${dosyaAdi}.xlsx`);
    return null;
    } finally {
      setPdfHazirlaniyor(false);
    }
  }, [veri, seciliAy, oncekiAy, dosyaAdi, reklamlar, reklamlariGetir, donem]);

  /**
   * PDF — A4 yatay iki sayfa. Ekranın kendisi değil, ekran dışında duran
   * iki ölçülü blok (`pdfSayfa1Ref` / `pdfSayfa2Ref`) çekilir: ekrandaki
   * başlıkta ay düğmeleri ve bu menü var, kağıtta anlamsız kalıyor.
   *
   * 🔴 Metin neden görüntü olarak gidiyor: jsPDF'in gömülü yazı tipleri
   * WinAnsi kodlamalı, ş/ğ/ı basamıyor — programatik metin yazılsa PDF'te
   * "Harcama" yerine bozuk karakter çıkardı.
   *
   * Her blok A4 yatayın tam oranında (1123 × 794) sabittir ve sayfayı
   * kenardan kenara doldurur — beyaz çerçeveyi bloğun kendi iç boşluğu
   * verir. İçerik sayfaya sığmıyorsa (ör. altı aylık ızgara + notlar)
   * `sayfayaSigdir` içeriği önce GENİŞLETİP sonra küçültür: böylece hem
   * yükseklik tutar hem de sayfanın eni tam dolar, yanlarda bant kalmaz.
   */
  /**
   * PDF'e girecek kreatiflerin görsellerini `data:` URI olarak gömer.
   *
   * 🔴 Şart: kartlardaki görseller YouTube/Google/TikTok CDN'lerinden geliyor;
   * html2canvas çapraz kaynak görseli çizince canvas "taint" oluyor ve
   * `toDataURL` istisna atıyor — tek bir dış görsel bütün PDF'i düşürür.
   * Bayt indirme kendi alan adımızdan (`/api/group/gorsel` proxy'si veya
   * Meta'nın mevcut küçük resim ucu) çerezle yapılır, sonuç base64 gömülür.
   */
  const kreatifleriGom = useCallback(async (kaynak) => {
    const rek = kaynak || reklamlar;
    if (!rek?.brands?.length) return null;

    /* 🔑 Kağıda artık BÜTÜN kreatifler giriyor, o yüzden bütün görseller
       gömülüyor (eskiden mecra başına ilk 6'sı). Aynı görselin iki kayıtta
       görünmesi mümkün — anahtar bazında tekilleştiriliyor ki 400+ kreatifte
       gereksiz indirme olmasın. */
    const isler = [];
    const gorulen = new Set();
    for (const b of rek.brands) {
      for (const mc of MECRALAR) {
        for (const r of b.platforms?.[mc.key]?.ads || []) {
          const url = kreatifGorsel(r);
          const anahtar = `${r.platform}-${r.id}`;
          if (!url || gorulen.has(anahtar)) continue;
          gorulen.add(anahtar);
          isler.push({ anahtar, url });
        }
      }
    }

    const gomulu = {};
    const indir = async ({ anahtar, url }) => {
      const istek = url.startsWith(API_BASE)
        ? url
        : `${API_BASE}/api/group/gorsel?u=${encodeURIComponent(url)}`;
      try {
        const res = await fetch(istek, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (blob.size < 200) return; // Meta boş görselde 1×1 saydam png döndürüyor
        const kucuk = await gorseliKucult(blob);
        if (kucuk) gomulu[anahtar] = kucuk;
      } catch (_) { /* görselsiz kart basılır */ }
    };

    for (let i = 0; i < isler.length; i += 10) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(isler.slice(i, i + 10).map(indir));
      setPdfIlerleme(`görseller ${Math.min(i + 10, isler.length)}/${isler.length}`);
    }

    // Kreatifleri gömülü görselle birlikte sayfalara hazırla
    return rek.brands
      .filter((b) => b.total > 0)
      .map((b) => ({
        ...b,
        platforms: Object.fromEntries(
          MECRALAR.map((mc) => [
            mc.key,
            {
              ...(b.platforms?.[mc.key] || {}),
              ads: (b.platforms?.[mc.key]?.ads || []).map((r) => ({
                ...r,
                gomulu: gomulu[`${r.platform}-${r.id}`] || null,
              })),
            },
          ])
        ),
      }));
  }, [reklamlar]);

  const pdfIndir = useCallback(async (opts) => {
    const dondur = opts?.dondur === true;
    if (!veri || pdfHazirlaniyor) return null;
    setMenuAcik(false);
    setPdfHazirlaniyor(true);
    try {
      /* Kreatif listesi ayrı uçtan geldiği için panel açılır açılmaz PDF
         alındığında henüz hazır olmayabiliyordu — o durumda PDF kreatifsiz
         çıkıyordu. Yoksa burada bekleyip çekiyoruz. */
      let rekVeri = reklamlar;
      if (!rekVeri?.brands?.length) rekVeri = await reklamlariGetir(false);

      /* 🔴 Yorum burada da ÜRETİLMEZ (bkz. `excelIndir`): yoksa PDF yorumsuz
         basılır. Model yalnızca "Yorumla" düğmesiyle çalışır. */

      // Kreatif sayfaları yalnızca dışa aktarım sırasında DOM'a giriyor:
      // yüzlerce base64 görseli sürekli bellekte tutmanın anlamı yok.
      const kreatifSayfalari = await kreatifleriGom(rekVeri);
      if (kreatifSayfalari?.length) {
        setPdfKreatifleri(kreatifSayfalari);
        // React'in basması + görsellerin çözülmesi için iki kare bekle
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        await new Promise((r) => setTimeout(r, 250));
      }

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const sayfaW = pdf.internal.pageSize.getWidth();
      const sayfaH = pdf.internal.pageSize.getHeight();
      const bloklar = Array.from(pdfKapRef.current?.querySelectorAll('[data-pdf-sayfa]') || []);
      for (let i = 0; i < bloklar.length; i++) {
        const el = bloklar[i];
        setPdfIlerleme(`sayfa ${i + 1}/${bloklar.length}`);
        sayfayaSigdir(el);
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: PDF_GENISLIK,
          height: PDF_YUKSEKLIK,
          windowWidth: PDF_GENISLIK,
          windowHeight: PDF_YUKSEKLIK,
        });
        if (i > 0) pdf.addPage();
        // Blok sayfayla aynı oranda; kenardan kenara basılır.
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, sayfaW, sayfaH);
      }
      if (dondur) {
        // `datauristring` → "data:application/pdf;filename=...;base64,XXX"
        const uri = pdf.output('datauristring');
        return { ad: `${dosyaAdi}.pdf`, tur: 'application/pdf', base64: uri.slice(uri.indexOf(',') + 1) };
      }
      pdf.save(`${dosyaAdi}.pdf`);
    } catch (e) {
      // Kullanıcı boş dosyayla kalmasın; sebebi konsolda.
      console.error('[grup][pdf]', e);
      if (dondur) throw e;
      alert('PDF oluşturulamadı: ' + e.message);
    } finally {
      setPdfKreatifleri(null);
      setPdfIlerleme('');
      setPdfHazirlaniyor(false);
    }
  }, [veri, dosyaAdi, pdfHazirlaniyor, kreatifleriGom, reklamlar, reklamlariGetir, donem, seciliAy]);

  /* ── E-posta ───────────────────────────────────────────────────────
     Gövdeyi SUNUCU kurar (panelin kendi önbelleğinden); tarayıcı yalnızca
     alıcı, not ve ek dosyaları yollar. Ekler burada üretilir ki maildeki
     dosya ekranda indirilenin AYNISI olsun — sunucuda ikinci kez üretmek
     iki farklı gerçek doğururdu. */

  const [mailAcik, setMailAcik] = useState(false);
  const [mailAlici, setMailAlici] = useState('');
  const [mailKonu, setMailKonu] = useState('');
  const [mailNot, setMailNot] = useState('');
  const [mailExcel, setMailExcel] = useState(true);
  const [mailPdf, setMailPdf] = useState(true);
  const [mailDurum, setMailDurum] = useState(null); // {tip:'bilgi'|'hata'|'ok', metin}
  const [mailGonderiliyor, setMailGonderiliyor] = useState(false);

  const mailiAc = useCallback(() => {
    setMailDurum(null);
    setMailKonu(`${veri?.group?.name || 'Grup'} — ${ayEtiketi(seciliAy)} özeti`);
    setMailAcik(true);
  }, [veri, seciliAy]);

  const mailGonder = useCallback(async () => {
    // Virgül, noktalı virgül, boşluk ve satır sonuyla ayrılmış adresler.
    const alicilar = mailAlici.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!alicilar.length) {
      setMailDurum({ tip: 'hata', metin: 'En az bir alıcı yazın.' });
      return;
    }
    setMailGonderiliyor(true);
    setMailDurum({ tip: 'bilgi', metin: 'Hazırlanıyor…' });
    try {
      const ekler = [];
      if (mailExcel) {
        setMailDurum({ tip: 'bilgi', metin: 'Excel hazırlanıyor…' });
        const x = await excelIndir({ dondur: true });
        if (x) ekler.push(x);
      }
      if (mailPdf) {
        // PDF kreatif görsellerini gömdüğü için en uzun adım (~20-30 sn).
        setMailDurum({ tip: 'bilgi', metin: 'PDF hazırlanıyor… (biraz sürebilir)' });
        const d = await pdfIndir({ dondur: true });
        if (d) ekler.push(d);
      }

      setMailDurum({ tip: 'bilgi', metin: 'Gönderiliyor…' });
      const res = await fetch(`${API_BASE}/api/group/hupalupa/mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          period: donem,
          donem: seciliAy,
          count: veri?.months?.length,
          alicilar,
          konu: mailKonu,
          not: mailNot,
          ekler,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setMailDurum({
        tip: 'ok',
        metin: `Gönderildi — ${json.gonderilen.join(', ')}${
          json.ekAdedi ? ` (${json.ekAdedi} ek)` : ' (eksiz)'
        }`,
      });
      setMailAlici('');
      setMailNot('');
    } catch (e) {
      setMailDurum({ tip: 'hata', metin: e.message });
    } finally {
      setMailGonderiliyor(false);
    }
  }, [mailAlici, mailKonu, mailNot, mailExcel, mailPdf, excelIndir, pdfIndir, donem, seciliAy, veri]);

  /** Ham CSV — Excel'i açamayan/otomasyona bağlayan için harcama ızgarası. */
  const csvIndir = useCallback(() => {
    if (!veri) return;
    const satirlar = [['Marka', 'Mecra', ...veri.months.map((m) => ayBasligiTam(m))]];
    for (const b of veri.brands) {
      satirlar.push([b.label, 'TOPLAM', ...veri.months.map((m) => Math.round(b.months[m].spend))]);
      for (const mc of MECRALAR) {
        const varMi = veri.months.some((m) => b.months[m].platforms?.[mc.key]?.spend);
        if (!varMi) continue;
        satirlar.push([
          b.label,
          mc.label,
          ...veri.months.map((m) => Math.round(b.months[m].platforms?.[mc.key]?.spend || 0)),
        ]);
      }
    }
    satirlar.push(['GRUP TOPLAM', 'TOPLAM', ...veri.months.map((m) => Math.round(veri.totals[m].spend))]);

    const csv = satirlar.map((r) => r.join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dosyaAdi}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setMenuAcik(false);
  }, [veri, dosyaAdi]);

  if (yukleniyor && !veri) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-500">
        <Loader2 className="h-7 w-7 animate-spin" />
        <div className="text-sm">Dört markanın mecra hesapları canlı okunuyor…</div>
      </div>
    );
  }

  if (hata && !veri) {
    return (
      <div className="mx-auto mt-10 max-w-lg rounded-xl bg-rose-50 p-5 ring-1 ring-rose-200">
        <div className="flex items-center gap-2 font-semibold text-rose-900">
          <AlertTriangle className="h-4 w-4" /> Panel yüklenemedi
        </div>
        <p className="mt-1 text-sm text-rose-800">{hata}</p>
        <button
          onClick={() => getir(true)}
          className="mt-3 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
        >
          Tekrar dene
        </button>
      </div>
    );
  }

  if (!veri || !seciliAy) return null;

  const tumHatalar = veri.brands.flatMap((b) =>
    (b.errors || []).map((e) => ({ marka: b.label, ...e }))
  );

  // Yorum SEÇİLİ döneme aittir; başka dönemin yorumu gösterilmez.
  const seciliYorum = yorumlar[`${donem}:${seciliAy}`] || null;

  /* ── PDF sayfa planı ────────────────────────────────────────────────
     Sayfa numarası tek bir yerden çıksın diye önce PLAN kurulur, sonra plan
     basılır. Sabit sayı tutulamıyor: kreatif listesi kesilmediği için bir
     markanın kaç sayfa tuttuğunu ancak bölme işlemi biliyor.

     🔑 Plan hem dışa aktarım sırasında (`pdfKreatifleri`, görselleri gömülü)
     hem de menüdeki sayfa sayısı için (`reklamlar`) aynı şekilde kurulur —
     satır sayısı ikisinde de aynı, aradaki tek fark gömülü görsel. */
  const pdfKaynak = pdfKreatifleri || (reklamlar?.brands || []).filter((b) => b.total > 0);
  const pdfPlani = [{ tip: 'ozet' }, { tip: 'izgara' }];
  if (seciliYorum) pdfPlani.push({ tip: 'yorum' });
  for (const b of pdfKaynak) {
    const gruplar = mecraGruplari(b, mecraToplamlari?.[b.id]);
    const tumSatirlar = gruplar.flatMap((g) => g.ads);
    /* Bayraklar MARKANIN TAMAMINDAN: sütun sayısı ve dipnotlar sayfadan
       sayfaya değişemez. */
    const bayraklar = {
      etkilesimVar: tumSatirlar.some((r) => r.metrics?.engagement != null),
      toplanamazVar: tumSatirlar.some((r) => r.metrics && !r.metrics.kesin),
      beyanSatisVar: tumSatirlar.some((r) => r.metrics?.resultLabel === 'Purchases'),
    };
    pdfPlani.push({ tip: 'marka', marka: b });
    const sayfalar = pdfKreatifSayfalari(gruplar);
    sayfalar.forEach((gr, i) =>
      pdfPlani.push({
        tip: 'kreatif',
        marka: b,
        gruplar: gr,
        bayraklar,
        parca: i + 1,
        parcaToplam: sayfalar.length,
      })
    );
  }
  const pdfToplamSayfa = pdfPlani.length;

  return (
    /* lang="tr" ŞART: başlıklarda CSS `uppercase` var ve tarayıcı Türkçe
       kuralını bilmezse "Önceki" → "ÖNCEKI", "Ciro" → "CIRO" diye noktasız
       basıyor. Sarmalayıcıdaki lang bunu düzeltiyor. */
    <div lang="tr" className="mx-auto max-w-[1400px] px-4 py-5 print:max-w-none print:px-0">
      {/* ── Başlık ── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">{veri.group.name}</h1>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              canlı
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Dört marka tek şirket · {veri.group.note} ·{' '}
            {veri.cached ? 'önbellekten' : `${(veri.elapsedMs / 1000).toFixed(1)} sn'de çekildi`}
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          {/* Aylık ⇄ haftalık: panelin tamamı (kartlar, özet, ızgara) seçilen
              kipe göre yeniden çekilir; iki kipin önbelleği ayrı. */}
          <div className="flex overflow-hidden rounded-lg ring-1 ring-slate-200">
            {[
              { kip: 'ay', ad: 'Aylık' },
              { kip: 'hafta', ad: 'Haftalık' },
            ].map((x) => (
              <button
                key={x.kip}
                onClick={() => kipDegistir(x.kip)}
                className={`px-3 py-1.5 text-xs font-medium transition ${
                  donem === x.kip
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {x.ad}
              </button>
            ))}
          </div>

          <div className="flex overflow-hidden rounded-lg ring-1 ring-slate-200">
            {veri.months.map((m) => (
              <button
                key={m}
                onClick={() => setSeciliAy(m)}
                title={ayEtiketi(m)}
                className={`px-3 py-1.5 text-xs font-medium transition ${
                  m === seciliAy
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {donemDugmesi(m)}
              </button>
            ))}
          </div>
          <button
            onClick={() => { yorumlariUnut(); getir(true); }}
            disabled={yukleniyor}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
            title="Canlı yenile"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} />
          </button>
          {/* Faturalandırma ekranı — aynı harcamanın üzerine mecra ücreti,
              stopaj, KDV ve Glowy hakedişi bindirilmiş hâli. */}
          <a
            href="/grup/hupalupa/faturalandirma"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50"
          >
            <Receipt className="h-3.5 w-3.5" /> Faturalandırma
          </a>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuAcik((a) => !a)}
              disabled={pdfHazirlaniyor}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
            >
              {pdfHazirlaniyor ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              {pdfHazirlaniyor
                ? pdfIlerleme
                  ? `Hazırlanıyor · ${pdfIlerleme}`
                  : 'Hazırlanıyor…'
                : 'Dışa aktar'}
              <ChevronDown className={`h-3.5 w-3.5 transition ${menuAcik ? 'rotate-180' : ''}`} />
            </button>

            {menuAcik && (
              <div className="absolute right-0 z-20 mt-1.5 w-[19rem] rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200">
                <MenuSatiri
                  ikon={FileSpreadsheet}
                  baslik={`Excel (.xlsx) — ${seciliYorum ? 'beş' : 'dört'} sayfa`}
                  aciklama={`Özet${seciliYorum ? ' · yapay zekâ yorumu' : ''} · marka × mecra × ${D.ad()} · marka KPI detayı · nasıl okunur${
                    reklamlar?.total ? ` · ${tl(reklamlar.total, 0)} yayınlanan reklam (bağlantılı)` : ''
                  }.`}
                  onClick={excelIndir}
                />
                <MenuSatiri
                  ikon={FileText}
                  baslik={`PDF — A4 yatay, ${pdfToplamSayfa} sayfa`}
                  aciklama={`${ayEtiketi(seciliAy)} marka kartları + ${veri.months.length} ${D.ad()}lık ızgara${
                    seciliYorum ? ' + yapay zekâ yorumu' : ''
                  }${
                    reklamlar?.total
                      ? ` + marka başına aylık karşılaştırma ve mecra kırılımı + ${tl(
                          reklamlar.total,
                          0
                        )} kreatifin TAMAMI`
                      : ''
                  }. Paylaşmak için.`}
                  onClick={pdfIndir}
                />
                <MenuSatiri
                  ikon={Download}
                  baslik="CSV — ham harcama"
                  aciklama={`Yalnızca marka × mecra × ${D.ad()} harcaması; başka sisteme aktarmak için.`}
                  onClick={csvIndir}
                />
                <div className="my-1 border-t border-slate-100" />
                <MenuSatiri
                  ikon={Mail}
                  baslik="E-posta gönder"
                  aciklama={`${ayEtiketi(seciliAy)} özeti mailin gövdesinde; Excel ve PDF ek olarak eklenebilir.`}
                  onClick={() => { setMenuAcik(false); mailiAc(); }}
                />
                <MenuSatiri
                  ikon={Printer}
                  baslik="Yazdır"
                  aciklama="Tarayıcının kendi yazdırma penceresi."
                  onClick={() => {
                    setMenuAcik(false);
                    window.print();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── E-posta penceresi ── */}
      {mailAcik && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16 print:hidden"
          onClick={() => { if (!mailGonderiliyor) setMailAcik(false); }}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Raporu e-postayla gönder</div>
                <div className="text-[11px] text-slate-500">
                  {ayEtiketi(seciliAy)} özeti mailin gövdesinde yazar; işaretlediklerin ek olarak gider.
                </div>
              </div>
              <button
                onClick={() => setMailAcik(false)}
                disabled={mailGonderiliyor}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3 px-4 py-4">
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Alıcılar</span>
                <textarea
                  value={mailAlici}
                  onChange={(e) => setMailAlici(e.target.value)}
                  rows={2}
                  placeholder="ornek@firma.com, ikinci@firma.com"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                />
                <span className="text-[10px] text-slate-400">
                  Virgül, noktalı virgül ya da satır sonuyla ayır. En çok 10 adres.
                </span>
              </label>

              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Konu</span>
                <input
                  value={mailKonu}
                  onChange={(e) => setMailKonu(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Not (isteğe bağlı)</span>
                <textarea
                  value={mailNot}
                  onChange={(e) => setMailNot(e.target.value)}
                  rows={3}
                  placeholder="Mailin başında görünecek kısa açıklama."
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                />
              </label>

              <div>
                <div className="text-[11px] font-medium text-slate-600">Ekler</div>
                <div className="mt-1 space-y-1.5">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={mailExcel}
                      onChange={(e) => setMailExcel(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300"
                    />
                    Excel (.xlsx) — tam tablolar ve yayınlanan reklamların listesi
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={mailPdf}
                      onChange={(e) => setMailPdf(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300"
                    />
                    PDF — sunum düzeni, {pdfToplamSayfa} sayfa (bütün kreatifler dahil)
                  </label>
                </div>
                {mailPdf && (
                  <div className="mt-1 text-[10px] text-slate-400">
                    PDF bütün kreatiflerin görsellerini gömüyor; hazırlanması bir dakikayı
                    bulabilir. Ekler toplamı 15 MB'ı aşarsa gönderim reddedilir — o durumda
                    yalnızca Excel işaretle.
                  </div>
                )}
              </div>

              {mailDurum && (
                <div
                  className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] ${
                    mailDurum.tip === 'hata'
                      ? 'bg-rose-50 text-rose-700'
                      : mailDurum.tip === 'ok'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-50 text-slate-600'
                  }`}
                >
                  {mailDurum.tip === 'ok' ? (
                    <Check className="mt-px h-3.5 w-3.5 shrink-0" />
                  ) : mailDurum.tip === 'hata' ? (
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <Loader2 className="mt-px h-3.5 w-3.5 shrink-0 animate-spin" />
                  )}
                  <span>{mailDurum.metin}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
              <button
                onClick={() => setMailAcik(false)}
                disabled={mailGonderiliyor}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Kapat
              </button>
              <button
                onClick={mailGonder}
                disabled={mailGonderiliyor || !mailAlici.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {mailGonderiliyor ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Mail className="h-3.5 w-3.5" />
                )}
                {mailGonderiliyor ? 'Gönderiliyor…' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Grup özeti ── */}
      <div className="mt-4">
        <GrupOzeti veri={veri} seciliAy={seciliAy} oncekiAy={oncekiAy} />
      </div>

      {/* ── Yapay zekâ yorumu ── */}
      <div className="mt-4">
        <YorumKarti
          yorum={seciliYorum}
          yukleniyor={yorumYukleniyor}
          hata={yorumHatasi}
          yenile={() => yorumGetir(seciliAy, !!seciliYorum)}
          donemEtiketi={ayEtiketi(seciliAy)}
        />
      </div>

      {/* ── Marka kartları ── */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {veri.brands.map((b) => (
          <MarkaKarti key={b.id} marka={b} ay={seciliAy} oncekiAy={oncekiAy} />
        ))}
      </div>

      {/* ── Marka × mecra × ay ızgarası ── */}
      <div className="mt-5">
        <IzgaraTablosu veri={veri} seciliAy={seciliAy} />
      </div>


      {/* ── Dönemde yayınlanan reklamlar ── */}
      <div className="mt-5 print:hidden">
        <YayindakiReklamlar
          veri={reklamlar}
          yukleniyor={reklamYukleniyor}
          hata={reklamHatasi}
          yenile={() => reklamlariGetir(true)}
          donemEtiketi={ayEtiketi(seciliAy)}
          mecraToplamlari={mecraToplamlari}
          izgara={veri}
          seciliAy={seciliAy}
        />
      </div>

      {/* ── Okuma notu ── */}
      <div className="mt-4">
        <OkumaNotu aylar={veri.months} />
      </div>

      {tumHatalar.length > 0 && (
        <div className="mt-3">
          <HataListesi hatalar={tumHatalar} />
        </div>
      )}

      {/* ── PDF için ekran dışı sayfalar ────────────────────────────────
          Ekranda görünmez ama YERLEŞİMİ vardır — `display:none` olsaydı
          html2canvas boş çekerdi, o yüzden ekranın dışına itiliyor.
          Genişlik A4 yatayın piksel karşılığına sabit; sütun sayıları da
          sabit (`dortSutun`, `grid-cols-4`) çünkü Tailwind kırılımları
          kabın değil TARAYICI penceresinin genişliğine bakar — dar
          ekrandan alınan PDF aksi halde iki sütuna düşerdi. */}
      <div
        ref={pdfKapRef}
        aria-hidden="true"
        className="print:hidden"
        style={{ position: 'fixed', top: 0, left: -30000, zIndex: -1 }}
      >
        {/* 🔴 html2canvas `box-shadow` BASMAZ — Tailwind'in `ring-*` ve
            `shadow-*` çerçeveleri box-shadow olduğu için PDF'te kartlar,
            tablo ve not kutusu çerçevesiz/havada duruyordu. Aynı çizgiyi
            gerçek `border` olarak veriyoruz; yalnızca PDF bloklarında. */}
        <style>{`
          [data-pdf-ic] .ring-1 { border: 1px solid #E2E8F0; }
          [data-pdf-ic] .ring-slate-200 { border-color: #E2E8F0; }
          [data-pdf-ic] .ring-emerald-200 { border-color: #A7F3D0; }
          [data-pdf-ic] .ring-rose-200 { border-color: #FECDD3; }
          [data-pdf-ic] .ring-amber-200 { border-color: #FDE68A; }
        `}</style>
        {/* 🔑 Sayfalar PLANDAN basılır (`pdfPlani`): numara = plandaki sıra.
            Marka sayfaları iki tip: aylık karşılaştırma (mecra kırılımı AÇIK)
            ve kreatif tablosu. Kreatifler kağıtta KESİLMEZ, taşan satırlar
            devam sayfalarına akar. */}
        {pdfPlani.map((sf, i) => {
          const baslik = (
            <PdfBaslik
              veri={veri}
              seciliAy={seciliAy}
              oncekiAy={oncekiAy}
              sayfa={i + 1}
              toplamSayfa={pdfToplamSayfa}
            />
          );

          if (sf.tip === 'ozet') {
            return (
              <PdfSayfa key="ozet">
                {baslik}
                <div className="mt-4">
                  <GrupOzeti veri={veri} seciliAy={seciliAy} oncekiAy={oncekiAy} dortSutun />
                </div>
                {/* `flex-1` + kartların `h-full`ü: artan boşluk kartlara
                    dağılır, sayfanın altında boş bant kalmaz. */}
                <div className="mt-3 grid min-h-0 flex-1 auto-rows-fr grid-cols-4 gap-3">
                  {veri.brands.map((b) => (
                    <MarkaKarti key={b.id} marka={b} ay={seciliAy} oncekiAy={oncekiAy} />
                  ))}
                </div>
              </PdfSayfa>
            );
          }

          if (sf.tip === 'izgara') {
            return (
              <PdfSayfa key="izgara">
                {baslik}
                <div className="mt-4">
                  <IzgaraTablosu veri={veri} seciliAy={seciliAy} />
                </div>
                <div className="mt-3 flex flex-1 flex-col justify-end gap-3">
                  <OkumaNotu aylar={veri.months} />
                  {tumHatalar.length > 0 && <HataListesi hatalar={tumHatalar} />}
                </div>
              </PdfSayfa>
            );
          }

          /* Yorum sayfası — yalnızca yorum üretildiyse. Sayfa yüksekliği sabit
             olduğu için uzun yorumda `sayfayaSigdir` içeriği ölçekler. */
          if (sf.tip === 'yorum') {
            return (
              <PdfSayfa key="yorum">
                {baslik}
                <div className="mt-4">
                  <YorumKarti yorum={seciliYorum} donemEtiketi={ayEtiketi(seciliAy)} pdf />
                </div>
              </PdfSayfa>
            );
          }

          /* Marka sayfası: kreatiften bağımsız aylık karşılaştırma.
             🔑 Kırılım kağıtta AÇIK — ekranda düğmeyle görülen mecra mecra
             dağılım da dosyaya girsin diye (Efe: "pdf eksiksiz olmalı").
             Kendi sayfasında duruyor: kırılımla birlikte tablo 30+ satır,
             kreatiflerle aynı sayfada okunmaz hale geliyordu. */
          if (sf.tip === 'marka') {
            const iz = izgaraMarka(veri, sf.marka.id);
            return (
              <PdfSayfa key={`marka-${sf.marka.id}`}>
                {baslik}
                <PdfMarkaSeridi
                  marka={sf.marka}
                  donemEtiketi={ayEtiketi(seciliAy)}
                  sag={`${veri.months.length} ${D.ad()}lık karşılaştırma · mecra kırılımı`}
                />
                {iz && (
                  <div className="mt-3">
                    <AylikKarsilastirma
                      marka={iz}
                      aylar={veri.months}
                      seciliAy={seciliAy}
                      pdf
                      kirilimZorla
                    />
                  </div>
                )}
              </PdfSayfa>
            );
          }

          // Kreatif sayfası (bir markada birden çok olabilir).
          return (
            <PdfSayfa key={`kreatif-${sf.marka.id}-${sf.parca}`}>
              {baslik}
              <PdfMarkaSeridi marka={sf.marka} donemEtiketi={ayEtiketi(seciliAy)} />
              <div className="mt-3">
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="text-xs font-semibold text-slate-700">
                    {ayEtiketi(seciliAy)} döneminde yayınlanan reklamlar
                  </span>
                  {sf.parcaToplam > 1 && (
                    <span className="text-[10px] text-slate-400">
                      liste {sf.parca}/{sf.parcaToplam}
                    </span>
                  )}
                </div>
                <MarkaReklamTablosu
                  marka={sf.marka}
                  mecraToplam={mecraToplamlari?.[sf.marka.id]}
                  pdf
                  /* Dilim zaten sayfaya göre bölündü: tablonun kendi sınırı
                     devreye girmesin, yoksa satırlar ikinci kez kırpılır. */
                  sinir={Number.MAX_SAFE_INTEGER}
                  gruplarHazir={sf.gruplar}
                  bayraklar={sf.bayraklar}
                />
              </div>
            </PdfSayfa>
          );
        })}
      </div>
    </div>
  );
}
